import React, { useState, useEffect, useContext, useRef, useMemo } from "react";
import { useTranslation } from 'react-i18next';
import dayjs from "dayjs";
import {
  Flex,
  Modal, ModalContent, ModalBody, ModalHeader, ModalFooter, ModalOverlay,
  Text,
  HStack,
  Button, ButtonGroup,
  Table, Thead, Tbody, Tfoot, Tr, Th, Td, TableContainer,
  Tooltip,
  Radio, RadioGroup,
  useToast,
  Switch,
  Popover, PopoverTrigger, Portal, PopoverContent, PopoverArrow, PopoverHeader, PopoverCloseButton, PopoverBody,
  ListItem,
  OrderedList,
  Menu, MenuButton, MenuList, MenuItem,
  Checkbox,
  IconButton,
  AlertDialog, AlertDialogBody, AlertDialogFooter, AlertDialogHeader, AlertDialogContent, AlertDialogOverlay,
  useDisclosure,
} from "@chakra-ui/react";
import { CheckIcon, ChevronDownIcon, ChevronUpIcon } from "@chakra-ui/icons";
import '../common.css';
import Decimal from "decimal.js";
import { FaRegQuestionCircle, FaExclamationCircle } from "react-icons/fa";
import GoogleAPI, { CellFormula, CellFormatRange, DataValidationRange, GoogleAPIBatchCollector } from "../../utils/GoogleAPI";
import { WebAPIContext } from "../../contexts/WebAPIContext";
import { ProjectContext } from "./InvestmentScreeningDetail";
import { Messages } from "../../contexts/constants";
import { sanitizeFileName, resolveDisplayPartNo } from "../../utils/utils";
import {
  ProcessBomCommonRow,
  ProcessBomInitialRow,
  ProcessBomInitialSheetData,
  MaterialCostRow,
  MaterialCostSheetData,
  CycletimeRow,
  CycletimeSheetData,
  YieldRow,
  YieldSheetData,
  BomSheetsData,
  BomData,
  Currency,
  PagePermissions,
} from "../../contexts/types";
import HelpIcon from "./HelpIcon";

// types.tsから移動した型をre-export
export type {
  ProcessBomCommonRow,
  ProcessBomInitialRow,
  ProcessBomInitialSheetData,
  MaterialCostRow,
  MaterialCostSheetData,
  CycletimeRow,
  CycletimeSheetData,
  YieldRow,
  YieldSheetData,
  BomSheetsData,
  BomData,
};

/**
 * タブ種別
 */
export type ProcessBomTabType = "ProcessBOM(initial)" | "MaterialCost" | "Cycletime" | "Yield";

/**
 * タブ情報
 */
interface TabInfo {
  /** タブID */
  id: ProcessBomTabType;
  /** 表示名 */
  label: string;
  /** 対応するバリデーションエラーのシートキー */
  sheetKey: keyof ValidationErrors;
}

// ============================================
// バリデーションエラー型定義
// ============================================

/**
 * セルエラー情報
 */
export interface CellError {
  /** エラーメッセージ */
  message: string;
}

/**
 * 行のエラー情報（キー：フィールド名、値：エラー情報）
 */
export type RowErrors = Record<string, CellError>;

/**
 * シートのエラー情報（キー：行インデックス、値：行のエラー情報）
 */
export type SheetErrors = Record<number, RowErrors>;

/**
 * 全シートのバリデーションエラー
 */
export interface ValidationErrors {
  /** ProcessBOM(initial)シートのエラー */
  processBom: SheetErrors;
  /** MaterialCostシートのエラー */
  materialCost: SheetErrors;
  /** Cycletimeシートのエラー */
  cycletime: SheetErrors;
  /** Yieldシートのエラー */
  yield: SheetErrors;
}

/**
 * パラメータ
 */
interface Props {
  /** プロジェクトID */
  projectId: string;

  /** パーツNo */
  partNo: string | null | undefined;

  /** 表示用パーツNo（No.435対応、未設定時はpartNoにフォールバック表示） */
  displayPartNo?: string | null;

  /** 労務費単価（賃率） */
  laborCostRate: number | null | undefined;

  /** Overhead単価 */
  overheadRate: number | null | undefined;

  /** 開始日 */
  startDate: string | null | undefined;

  /** 終了日 */
  endDate: string | null | undefined;

  /** 通貨マスタ情報 */
  currencyList: Currency[];

  /** 全シートデータ */
  onSave: (bomSheetsData: BomSheetsData) => Promise<{ resultMaterialCostData: boolean, resultProcessData: boolean }>;

  /** 権限用 画面パーミッション */
  pagePermissions: PagePermissions | undefined;

  /** 表示制御対応フラグ */
  isOpen: boolean;

  /** 非表示処理 */
  onClose: () => void;
}

/** タブ一覧 */
const TABS: TabInfo[] = [
  { id: "ProcessBOM(initial)", label: "ProcessBOM(initial)", sheetKey: "processBom" },
  { id: "MaterialCost", label: "MaterialCost", sheetKey: "materialCost" },
  { id: "Cycletime", label: "Cycletime", sheetKey: "cycletime" },
  { id: "Yield", label: "Yield", sheetKey: "yield" },
];

// ============================================
// メインコンポーネント
// ============================================

/**
 * BOMデータ一覧モーダル
 */
const BomInfoList: React.FC<Props> = (props) => {
  const { t } = useTranslation();

  /** 画面名 */
  const TITLE = t('features.InvestmentScreening.BomInfoList.title');
  /** i18nキープレフィックス */
  const bl = "features.InvestmentScreening.BomInfoList";

  /** スプレッドシート格納先 */
  const SPREAD_SHEET_PATH = "CEAS/SpreadSheet/ProductionProcess";

  /** 幅 - SEQ */
  const WIDTH_SEQ = 70;
  /** 幅 - LEVEL */
  const WIDTH_LEVEL = 130;
  /** 幅 - MODEL NO */
  const WIDTH_MODEL_NO = 150;
  /** 幅 - PROCESS CODE */
  const WIDTH_PROCESS_CODE = 120;
  /** 幅 - ITEM NO */
  const WIDTH_ITEM_NO = 250;
  /** 幅 - ITEM NAME */
  const WIDTH_ITEM_NAME = 250;

  /** 幅 - 購入通貨 */
  const WIDTH_PURCHASE_CURRENCY = 120;
  /** 幅 - Usage */
  const WIDTH_USAGE = 100;
  /** 幅 - タイプ */
  const WIDTH_TYPE = 120;

  /** 幅 - U/P */
  const WIDTH_UNIT_PRICE = 150;
  /** 幅 - C/T */
  const WIDTH_CYCLE_TIME = 150;
  /** 幅 - 歩留 */
  const WIDTH_YIELD = 150;

  /** 表 行の高さ */
  const HEIGHT_ROW = 37;

  /** 初期行 */
  const INITIAL_ROWS = 500;

  /** カラム名 */
  const columnNames = {
    common: {
      Seq: "Seq.",
      Level: "Level",
      ModelNo: "Model No.",
      ProcessCode: "Process Code",
      ItemNo: "Item No.",
      ItemName: "Item Name",
    },
    processBom: {
      IsCustomerSpecifiedParts: t(`${bl}.column.customerSpecifiedParts`),
    },
    materialCost: {
      UnitPriceCombined: "U/P (Combined)",
      UnitPriceInhouse: "U/P(inhouse)",
      UnitPriceExternal: "U/P(external)",
      InhouseRatio: "Inhouse Ratio",
      MaterialYield: "Material Yield",
      Amount: "Amount",
      PurchaseCurrency: t(`${bl}.column.purchaseCurrency`),
      Usage: "Usage",
      Type: "Type",
    },
    cycletime: {
      CycleTime: "CycleTime",
    },
    yield: {
      Yield: "Yield",
    },
  }

  /** シート名 */
  const sheetNames = {
    processBom: "ProcessBOM(initial)",
    materialCost: "MaterialCost",
    cycletime: "Cycletime",
    yield: "Yield",
  }

  /**
   * デフォルト BOMシート情報
   * @returns 
   */
  const getDefaultBomSheetsData = () => {
    return {
      processBom: {
        dataRows: [],
      },
      materialCost: {
        dataRows: [],
      },
      cycletime: {
        dataRows: [],
      },
      yield: {
        dataRows: [],
      }
    };
  }

  /** トースト表示用 */
  const toast = useToast();

  /** 通信オブジェクト */
  const WebAPI = useContext(WebAPIContext);
  /** 振り返りモード */
  const projectContext = useContext(ProjectContext);
  const reviewDate = projectContext?.reviewDate ?? null;

  /** Google API汎用処理クラス */
  const googleAPI: GoogleAPI = new GoogleAPI();

  /** 現在選択中のタブ */
  const [activeTab, setActiveTab] = useState<ProcessBomTabType>("ProcessBOM(initial)");

  /** GoogleSpreadボタン制御 */
  const [isLoading, setIsLoading] = useState(false);

  /** 更新日時 */
  const [updateDate, setUpdateDate] = useState<string | null | undefined>("");

  /** 画面表示データ */
  const [bomSheetsData, setBomSheetsData] = useState<BomSheetsData>(getDefaultBomSheetsData());

  /** 年月リスト */
  const [yearMonthList, setYearMonthList] = useState<string[]>([]);

  /** スプレッドシートウィンドウ管理 */
  const [spreadSheetWindows, setSpreadSheetWindows] = useState<Window | null>(null);

  /** スプレッドシートウィンドウ管理（クローズ判定） */
  const [isClosedSpreadSheetWindows, setIsClosedSpreadSheetWindows] = useState<boolean>(false);

  /** アクティブなスプレッドシートID */
  const [activeSpreadsheetId, setActiveSpreadsheetId] = useState<string | null>(null);

  /** BOMシート作成ボタン押下時の未連携変更ロスト防止確認ダイアログ */
  const { isOpen: isConfirmDialogOpen, onOpen: onOpenConfirmDialog, onClose: onCloseConfirmDialog } = useDisclosure();
  const confirmDialogCancelRef = useRef<HTMLButtonElement>(null);

  /** 確認ダイアログ承認時に実行するアクション（どちらのボタンから来たかを保持） */
  const [pendingOpenMode, setPendingOpenMode] = useState<"processBomOnly" | "allSheets" | null>(null);

  /** データ連携済みフラグ
   * - BOMシート作成ボタン押下でファイル作成した直後: false（未連携リスクあり）
   * - 「データ連携」成功時: true（未連携リスクなし）
   * - モーダル open 初期化時: true（判定なし扱い）
   */
  const [isDataLinked, setIsDataLinked] = useState<boolean>(true);

  /** 「全シート一括作成」ボタンの有効判定（ProcessBOMの材料行が1行以上選択されているか） */
  const isMaterialRowSelected = useMemo(() => {
    return (bomSheetsData?.processBom?.dataRows ?? []).some(r => r.isMaterialCostRow === true);
  }, [bomSheetsData?.processBom?.dataRows]);

  /** バリデーションエラー */
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({
    processBom: {},
    materialCost: {},
    cycletime: {},
    yield: {},
  });

  /** サイクルタイム単位（"minute" | "second" | ""(未選択)） */
  const [cycleTimeUnit, setCycleTimeUnit] = useState<"minute" | "second" | "">("");

  /** 表示列の状態管理 */
  const [visibleColumns, setVisibleColumns] = useState({
    level: true,
    modelNo: true,
    processCode: true,
    itemNo: true,
    itemName: true,
  });

  /** MaterialCostタブのTFoot展開状態 */
  const [isMaterialCostTotalExpanded, setIsMaterialCostTotalExpanded] = useState(true);

  /** 列表示設定の定義 */
  const columnVisibilityOptions = [
    { key: "level", label: "Level", width: WIDTH_LEVEL },
    { key: "modelNo", label: "Model No.", width: WIDTH_MODEL_NO },
    { key: "processCode", label: "Process Code", width: WIDTH_PROCESS_CODE },
    { key: "itemNo", label: "Item No.", width: WIDTH_ITEM_NO },
    { key: "itemName", label: "Item Name", width: WIDTH_ITEM_NAME },
  ] as const;

  /**
   * 初期表示
   */
  useEffect(() => {
    /**
     * GAPIロードとサインインを行う。
     */
    const initGapi = async () => {
      try {
        // Google API 初期化
        if (!await googleAPI.initGapi()) {
          throw new Error("initGapi error");
        }

        // Googleアカウント認証
        if (! await googleAPI.signIn()) {
          throw new Error("signIn error");
        }
      } catch (error) {
        console.error("Google API 初期化エラー:", error);
        toast({
          title: TITLE,
          description: t('features.InvestmentScreening.BomInfoList.message.googleApiInitFailed'),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    };

    initGapi();
  }, []);

  /**
   * データ取得
   */
  useEffect(() => {
    if (!props.isOpen) {
      // モーダル表示時でなければ、終了
      setBomSheetsData(getDefaultBomSheetsData());
      return;
    }

    if (!props.projectId || !props.partNo) {
      // キー情報が揃ってなければ、終了
      return;
    }

    const getData = async () => {
      // BOM情報取得
      const bomData = await getBomByProjectId(props.projectId, props.partNo!) as BomData[];

      // BOM情報 ⇒ 全シートデータの統合型 変換
      const bomSheetsData = convertBomSheetsData(bomData);
      setBomSheetsData(bomSheetsData);

      if (bomData.length > 0) {
        // 更新日時
        setUpdateDate(bomData[0].updateDate);
      }

      if (bomData.length > 0) {
        // 既存データありの場合、[秒]
        setCycleTimeUnit("second");
      }
    }

    // データ取得
    getData();

  }, [props.isOpen, props.projectId, props.partNo]);

  /**
   * 初期設定用の年月リストと各年月データをマージ
   */
  useEffect(() => {
    if (props.startDate && props.endDate && yearMonthList.length === 0) {
      // 年月リスト生成
      const yearMonthList = generateYearMonthList(props.startDate, props.endDate);
      setYearMonthList(yearMonthList);
    }

    if (bomSheetsData) {
      const monthlyDataOriginal: Record<string, string> = yearMonthList.reduce((acc, yearMonth) => {
        acc[yearMonth] = "";
        return acc;
      }, {} as Record<string, string>);

      // ■ MaterialCost（各年月別データプロパティをマージ）
      bomSheetsData.materialCost.dataRows.forEach((row: MaterialCostRow) => {
        // 各年月別データプロパティをマージ
        const mergeMonthlyData = (target: Record<string, string>) => {
          const merged = { ...monthlyDataOriginal };
          for (const [key, _] of Object.entries(merged || {})) {
            merged[key] = target[key] || "";
          }
          return merged;
        };

        row.unitPriceCombinedMonthly = mergeMonthlyData(row.unitPriceCombinedMonthly);
        row.unitPriceInhouseMonthly = mergeMonthlyData(row.unitPriceInhouseMonthly);
        row.unitPriceExternalMonthly = mergeMonthlyData(row.unitPriceExternalMonthly);
        row.inhouseRatioMonthly = mergeMonthlyData(row.inhouseRatioMonthly);
        row.materialYieldMonthly = mergeMonthlyData(row.materialYieldMonthly);
        row.amountMonthly = mergeMonthlyData(row.amountMonthly);
      });

      // ■ Cycletime
      bomSheetsData.cycletime.dataRows.forEach((row: CycletimeRow) => {
        const monthlyData = { ...monthlyDataOriginal };
        for (const [key, _] of Object.entries(monthlyData)) {
          monthlyData[key] = row.monthlyData[key] || "";
        }
        row.monthlyData = monthlyData;
      });

      // ■ Yield
      bomSheetsData.yield.dataRows.forEach((row: YieldRow) => {
        const monthlyData = { ...monthlyDataOriginal };
        for (const [key, _] of Object.entries(monthlyData)) {
          monthlyData[key] = row.monthlyData[key] || "";
        }
        row.monthlyData = monthlyData;
      });
    }
  }, [props.startDate, props.endDate, bomSheetsData]);

  /** モーダルが開いた時にタブをリセット */
  useEffect(() => {
    if (props.isOpen) {
      // 表示時
      setActiveTab("ProcessBOM(initial)");

      // バリデーションエラーを初期化
      setValidationErrors({
        processBom: {},
        materialCost: {},
        cycletime: {},
        yield: {},
      });

      // open 側でも未連携リスクフラグ関連を対称リセット（将来の改修でのバグ温床防止）
      setActiveSpreadsheetId(null);
      setIsDataLinked(true);
      setPendingOpenMode(null);
      onCloseConfirmDialog();
      setSpreadSheetWindows(null);
      setIsClosedSpreadSheetWindows(false);
    } else {
      // 非表示時
      if (spreadSheetWindows && !spreadSheetWindows.closed) {
        spreadSheetWindows?.close();
        setBomSheetsData({
          processBom: { dataRows: [] },
          materialCost: { dataRows: [] },
          cycletime: { dataRows: [] },
          yield: { dataRows: [] },
        });
      }
      // モーダル再オープン時に旧スプレッドシートIDを参照しないようクリア
      setActiveSpreadsheetId(null);
      // 未連携リスクフラグもリセット（次回open時は判定なし扱い）
      setIsDataLinked(true);
      setPendingOpenMode(null);
      // ダイアログ表示中にモーダルが閉じられた場合の残存防止
      onCloseConfirmDialog();
      // ウィンドウ状態もクリア（open時との対称性確保）
      setSpreadSheetWindows(null);
      setIsClosedSpreadSheetWindows(false);
    }
  }, [props.isOpen]);

  /**
   * 既存の同名ファイルを削除してから新規スプレッドシートを作成する。
   * （「ProcessBOM単独作成」/「全シート一括作成」の両ボタンから共通利用）
   * @param fileName ファイル名（sanitize済み）
   * @param parentId 親フォルダID
   * @param sharedDriveName 共有ドライブ名
   * @param sheetNamesToCreate 作成するシート名
   * @returns 新規スプレッドシートIDと、シート名→シートIDの対応表
   */
  const createFreshSpreadsheet = async (
    fileName: string,
    parentId: string,
    sharedDriveName: string | null,
    sheetNamesToCreate: string[]
  ): Promise<{ spreadsheetId: string; sheetNameToId: Record<string, number> }> => {
    // 既存ファイル検索・削除（1ファイルに保つ）
    const existingId = await googleAPI.getFileOrFolderId(fileName, "file", sharedDriveName, parentId);
    if (existingId) {
      try {
        await googleAPI.deleteFile(existingId);
      } catch (e) {
        console.error("既存ファイル削除に失敗", e);
        throw new Error(t(`${bl}.message.deleteSpreadsheetFailed`));
      }
    }
    // 新規作成（既存ファイルは削除済み。作成時にシート構成も指定し、シート追加・リネームの呼び出しを省略する）
    // ※ sharedDriveName は常に null（マイドライブ運用）のため、createSpreadSheetWithSheets には渡さない
    return await googleAPI.createSpreadSheetWithSheets(fileName, sheetNamesToCreate, parentId);
  };

  /**
   * スプレッドシートを開く内部共通処理。
   * mode に応じて生成するシート種別を切り替える。
   * @param mode "processBomOnly": ProcessBOM(initial)のみ / "allSheets": 4シート全て
   */
  const openSpreadsheetInternal = async (mode: "processBomOnly" | "allSheets") => {
    const functionName = `openSpreadsheetInternal(${mode})`;

    try {
      setIsLoading(true);

      // ポップアップブロック回避：非同期処理より前にユーザー操作起因でウィンドウを確保
      // 既存ウィンドウが生きていれば再利用、なければ新規ブランクタブを開く（"_blank" で別オリジン問題を回避）
      let newWindow: Window | null = (spreadSheetWindows && !spreadSheetWindows.closed)
        ? spreadSheetWindows
        : window.open("", "_blank");

      // Googleアカウント認証（念のため）
      if (! await googleAPI.signIn()) {
        throw new Error("signIn error");
      }

      // URLエンコード
      const spreadSheetPath = `${SPREAD_SHEET_PATH}/${encodeURIComponent(props.projectId)}`;

      // マイドライブに作成（権限制御のため共有ドライブは使用しない）
      const sharedDriveName: string | null = null;

      // スプレッドシート親フォルダID取得
      const parentId = await googleAPI.createFolderPath(spreadSheetPath, sharedDriveName);

      // 対象ファイル名（既存あれば削除→新規作成で常に1ファイル維持）
      const fileName = sanitizeFileName(resolveDisplayPartNo({ partNo: props.partNo || undefined, displayPartNo: props.displayPartNo || undefined }));
      // 作成するシート（モードによって生成対象を切り替え）
      const sheetsToAdd = mode === "allSheets"
        ? [sheetNames.processBom, sheetNames.materialCost, sheetNames.cycletime, sheetNames.yield]
        : [sheetNames.processBom];
      const { spreadsheetId, sheetNameToId } = await createFreshSpreadsheet(fileName, parentId!, sharedDriveName, sheetsToAdd);
      console.log(`${functionName} create spreadsheet with sheets OK`);

      // スプレッドシートIDを保持（作成成功後にのみ更新）
      setActiveSpreadsheetId(spreadsheetId);

      // 値・書式・数式・入力規則の書き込みを溜め、最後に flush() でまとめて送信する
      const batch = new GoogleAPIBatchCollector(spreadsheetId);

      // パーセンテージ表示のため、÷100する処理
      const div100 = (value: string) => {
        const numVal = Number(value);
        if ((value || "") === "" || isNaN(Number(value))) {
          return "";
        }
        return String(numVal / 100);
      }

      // シートIDは作成時の応答から取得（シート情報の再取得を省略）
      const sheetIds = {
        processBom: sheetNameToId[sheetNames.processBom],
        materialCost: sheetNameToId[sheetNames.materialCost],
        cycletime: sheetNameToId[sheetNames.cycletime],
        yield: sheetNameToId[sheetNames.yield],
      };

      // 入力規則のクリアは不要（createFreshSpreadsheet で常に新規作成しており、古い規則は存在しない）

      const headerRowCommon = Object.values(columnNames.common);

      // 配列リサイズ
      const resizeArray = (baseArray: string[], newLength: number) => Array.from({ length: newLength }, (_, i) => baseArray[i] ?? "");
      // 空配列作成
      const emptyArray = (length: number) => Array.from({ length: length }, () => "");

      // ■ ProcessBOM(initial) シート
      const headerRowForProcessBOM = [
        ...headerRowCommon,
        ...Object.values(columnNames.processBom) as string[]
      ];
      const heaerRowsForProcessBOM: string[][] = [
        resizeArray([resolveDisplayPartNo({ partNo: props.partNo || undefined, displayPartNo: props.displayPartNo || undefined })], headerRowForProcessBOM.length),
        headerRowForProcessBOM
      ];

      // 値設定（共通項目 + 客先指定部品 + Usage）
      await inputSpreadSheetData(
        spreadsheetId,
        sheetNames.processBom,
        heaerRowsForProcessBOM,
        bomSheetsData?.processBom.dataRows.map(row => ({
          ...row,
          isCustomerSpecifiedParts: row.isCustomerSpecifiedParts ? true : "", // trueのみ表示
        })),
        null,
        null,
        ["isMaterialCostRow"], // isMaterialCostRowは除外
        batch
      );
      console.log(`${functionName} ProcessBOM(initial) sheet set value OK`);

      // 入力規則設定（客先指定部品）- ProcessBOM(initial)シートに設定
      await batch.spreadSheetSetDataValidation(spreadsheetId, [
        ...generateDataValidation(
          sheetIds.processBom,
          headerRowForProcessBOM,
          [columnNames.processBom.IsCustomerSpecifiedParts],
          heaerRowsForProcessBOM.length + 1,
          bomSheetsData.processBom.dataRows.length || INITIAL_ROWS,
          ["TRUE", "FALSE"],
        ),
      ]);

      // allSheetsモード時のみ MaterialCost/Cycletime/Yield を生成
      if (mode === "allSheets") {
        // ■ MaterialCost シート
        const headerRowForMaterialCost = [
          ...Object.values(columnNames.common),
          columnNames.materialCost.PurchaseCurrency,
          columnNames.materialCost.Usage,
          columnNames.materialCost.Type,
        ];
        const headerRowsForMaterialCost = [
          resizeArray([...emptyArray(headerRowForMaterialCost.length - 1), columnNames.materialCost.UnitPriceInhouse], headerRowForMaterialCost.length + yearMonthList.length),
          resizeArray([...emptyArray(headerRowForMaterialCost.length - 1), columnNames.materialCost.UnitPriceExternal], headerRowForMaterialCost.length + yearMonthList.length),
          resizeArray([...emptyArray(headerRowForMaterialCost.length - 1), columnNames.materialCost.InhouseRatio], headerRowForMaterialCost.length + yearMonthList.length),
          resizeArray([...emptyArray(headerRowForMaterialCost.length - 1), columnNames.materialCost.MaterialYield], headerRowForMaterialCost.length + yearMonthList.length),
          [...headerRowForMaterialCost, ...yearMonthList,],
        ];

        // 値設定
        await inputSpreadSheetDataForMaterialCost(
          spreadsheetId,
          sheetNames.materialCost,
          headerRowsForMaterialCost,
          bomSheetsData?.materialCost.dataRows,
          div100,
          ["purchaseCurrencyId", "usage"],
          props.currencyList,
          batch,
        );
        console.log(`${functionName} MaterialCost sheet set value OK`);

        // 入力規則設定（購入通貨）- MaterialCostシートに設定
        // MaterialCostシートは材料費行が4行（U/P(inhouse), U/P(external), Inhouse Ratio, Material Yield）、
        // プロセス行が1行のため、実際のシート上の行数を計算する
        const currencyNameList = props.currencyList
          .filter(c => c.currencyName)
          .map(c => c.currencyName!);
        if (currencyNameList.length > 0) {
          let materialCostSheetRowCount = 0;
          if (bomSheetsData.materialCost.dataRows.length > 0) {
            for (let idx = 0; idx < bomSheetsData.materialCost.dataRows.length; idx++) {
              const isMaterialCostRow = bomSheetsData.processBom.dataRows[idx]?.isMaterialCostRow;
              materialCostSheetRowCount += isMaterialCostRow ? 4 : 1;
            }
          } else {
            materialCostSheetRowCount = INITIAL_ROWS;
          }
          await batch.spreadSheetSetDataValidation(spreadsheetId, [
            ...generateDataValidation(
              sheetIds.materialCost,
              headerRowForMaterialCost,
              [columnNames.materialCost.PurchaseCurrency],
              headerRowsForMaterialCost.length + 1,
              materialCostSheetRowCount,
              currencyNameList,
            ),
          ]);
        }

        // 数式設定（U/P Combined計算）
        await batch.spreadSheetSetFormulas(spreadsheetId, [
          ...generateCellForSumIf(sheetIds.materialCost, 1, headerRowForMaterialCost, columnNames.materialCost.UnitPriceInhouse, headerRowsForMaterialCost.length + 1),
          ...generateCellForSumIf(sheetIds.materialCost, 2, headerRowForMaterialCost, columnNames.materialCost.UnitPriceExternal, headerRowsForMaterialCost.length + 1),
          ...generateCellForProduct(sheetIds.materialCost, 3, headerRowForMaterialCost, columnNames.materialCost.InhouseRatio, headerRowsForMaterialCost.length + 1),
          ...generateCellForProduct(sheetIds.materialCost, 4, headerRowForMaterialCost, columnNames.materialCost.MaterialYield, headerRowsForMaterialCost.length + 1),
        ]);

        // 書式設定（パーセント列）
        await batch.spreadSheetSetCellFormat(spreadsheetId, [
          // ヘッダー部（U/P(inhouse)、U/P(external)）
          ...generateCellFormat(
            sheetIds.materialCost,
            [...headerRowForMaterialCost, ...yearMonthList],
            [yearMonthList[0]],
            1,
            2,
            {
              numberFormat: { type: "NUMBER", pattern: "0" }
            },
            yearMonthList.length
          ),
          // ヘッダー部（Inhouse Ratio）
          ...generateCellFormat(
            sheetIds.materialCost,
            [...headerRowForMaterialCost, ...yearMonthList],
            [yearMonthList[0]],
            3,
            1,
            {
              numberFormat: { type: "PERCENT", pattern: "0.00%" }
            },
            yearMonthList.length
          ),
          // ヘッダー部（Material Yield）
          ...generateCellFormat(
            sheetIds.materialCost,
            [...headerRowForMaterialCost, ...yearMonthList],
            [yearMonthList[0]],
            4,
            1,
            {
              numberFormat: { type: "PERCENT", pattern: "0.00%" }
            },
            yearMonthList.length
          ),
          // データ行
          ...generateCellFormatForMaterialCost(
            sheetIds.materialCost,
            [...headerRowForMaterialCost, ...yearMonthList],
            [yearMonthList[0]],
            headerRowsForMaterialCost.length + 1,
            1,
            {
              numberFormat: { type: "PERCENT", pattern: "0.00%" }
            },
            yearMonthList.length
          )
        ]);


        // ■ Cycletime シート
        const headerForCycletime = [...headerRowCommon, ...yearMonthList];
        const headerRowsForCycletime = [
          resizeArray([], headerRowCommon.length + yearMonthList.length),
          resizeArray([...emptyArray(headerRowCommon.length), columnNames.cycletime.CycleTime], headerRowCommon.length + yearMonthList.length),
          headerForCycletime,
        ];

        // 値設定
        await inputSpreadSheetData(
          spreadsheetId,
          sheetNames.cycletime,
          headerRowsForCycletime,
          bomSheetsData?.cycletime.dataRows,
          "monthlyData",
          null,
          undefined,
          batch
        );
        console.log(`${functionName} Cycletime sheet set value OK`);

        // 数式設定
        await batch.spreadSheetSetFormulas(spreadsheetId, [
          ...generateCellFormuras(
            sheetIds.cycletime,
            headerForCycletime,
            [...yearMonthList],
            4,
            1,
            "sum"
          )
        ]);

        // ■ Yield シート
        const headerForYield = [...headerRowCommon, ...yearMonthList];
        const headerRowsForYield = [
          resizeArray([], headerRowCommon.length + yearMonthList.length),
          resizeArray([...emptyArray(headerRowCommon.length), columnNames.yield.Yield], headerRowCommon.length + yearMonthList.length),
          headerForYield,
        ];

        // 値設定
        await inputSpreadSheetData(
          spreadsheetId,
          sheetNames.yield,
          headerRowsForYield,
          bomSheetsData?.yield.dataRows,
          "monthlyData",
          {
            [columnNames.yield.Yield]: div100,
            "monthlyData": div100,
          },
          undefined,
          batch
        );
        console.log(`${functionName} Yield sheet set value OK`);

        // 数式設定
        await batch.spreadSheetSetFormulas(spreadsheetId, [
          ...generateCellFormuras(
            sheetIds.yield,
            headerForYield,
            [...yearMonthList],
            headerRowsForYield.length + 1,
            1,
            "product"
          )
        ]);

        // 書式設定
        await batch.spreadSheetSetCellFormat(spreadsheetId, [
          ...generateCellFormat(
            sheetIds.yield,
            Object.values(headerForYield),
            [...yearMonthList],
            1,
            1,
            {
              numberFormat: { type: "PERCENT", pattern: "0.00%" }
            }
          ),
          ...generateCellFormat(
            sheetIds.yield,
            Object.values(headerForYield),
            [...yearMonthList],
            headerRowsForYield.length + 1,
            (bomSheetsData?.yield?.dataRows?.length || INITIAL_ROWS),
            {
              numberFormat: { type: "PERCENT", pattern: "0.00%" }
            }
          ),
        ]);


        // 書式設定(年月)
        const offsetColumn = Object.keys(columnNames.common).length + 1;
        await batch.spreadSheetSetCellFormat(spreadsheetId, [
          {
            sheetId: sheetIds.materialCost,
            startRow: headerRowsForMaterialCost.length,
            endRow: headerRowsForMaterialCost.length,
            startCol: offsetColumn,
            endCol: offsetColumn + yearMonthList.length,
            format: {
              numberFormat: {
                type: "DATE",
                pattern: "yyyy-mm"
              }
            }
          },
          {
            sheetId: sheetIds.cycletime,
            startRow: headerRowsForCycletime.length,
            endRow: headerRowsForCycletime.length,
            startCol: offsetColumn,
            endCol: offsetColumn + yearMonthList.length,
            format: {
              numberFormat: {
                type: "DATE",
                pattern: "yyyy-mm"
              }
            }
          },
          {
            sheetId: sheetIds.yield,
            startRow: headerRowsForYield.length,
            endRow: headerRowsForYield.length,
            startCol: offsetColumn,
            endCol: offsetColumn + yearMonthList.length,
            format: {
              numberFormat: {
                type: "DATE",
                pattern: "yyyy-mm"
              }
            }
          },
        ]);
      } // end if (mode === "allSheets")

      // 溜めた書き込みをまとめて送信（値書き込み → 書式・数式・入力規則 の順）
      await batch.flush();
      console.log(`${functionName} batch flush OK`);

      // 事前確保したウィンドウをスプレッドシートに遷移
      const spreadSheetUrl = googleAPI.getOpenSpradSheetUrl(spreadsheetId);
      if (newWindow) {
        newWindow.location.href = spreadSheetUrl;
      }

      if (newWindow) {
        // 既存ウィンドウの場合もアクティブにする
        newWindow.focus();

        if (spreadsheetId) {
          // スプレッドシートウィンドウを保持
          setSpreadSheetWindows(newWindow);
        }

        setIsClosedSpreadSheetWindows(false);

        // GoogleSpreadシートウィンドウを手動で閉じられた場合
        const interval = setInterval(() => {
          if (newWindow?.closed) {
            clearInterval(interval); // 監視を停止
            setIsClosedSpreadSheetWindows(true);
          }
        }, 500); // 500ms ごとに監視

        // ウィンドウが開けた場合のみ未連携リスクフラグをON
        // （ポップアップブロック時はユーザーが画面編集できないためダイアログ不要）
        setIsDataLinked(false);
      } else {
        console.warn("A pop-up from Google Sheets was blocked.");
      }

    } catch (error) {
      console.error(`${functionName} Error.`, error);
      spreadSheetWindows?.close();
      // 作成途中で失敗した場合、activeSpreadsheetId に新IDが残ると次回ガード判定が誤動作するためクリア
      setActiveSpreadsheetId(null);

      const message = error instanceof Error && error.message === t(`${bl}.message.deleteSpreadsheetFailed`)
        ? error.message
        : t(`${bl}.message.googleSpreadUnavailable`);

      toast({
        title: TITLE,
        description: message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * 「ProcessBOM単独作成」/「全シート一括作成」共通のガード処理。
   * 既に SpreadSheet 作成済み、かつ「データ連携」未実行（isDataLinked=false）なら
   * 未連携変更ロストの可能性があるため確認ダイアログを表示する。
   * @param mode 実行モード（processBomOnly / allSheets）
   */
  const guardOpenSpreadsheet = async (mode: "processBomOnly" | "allSheets") => {
    // 連打race対策（setIsLoading反映前のダブルクリックでファイル2重生成を防止）
    if (isLoading) return;
    if (activeSpreadsheetId && !isDataLinked) {
      setPendingOpenMode(mode);
      onOpenConfirmDialog();
      return;
    }
    await openSpreadsheetInternal(mode);
  };

  /**
   * 「ProcessBOM単独作成」ボタン押下
   * ProcessBOM(initial) シートのみ生成する。
   */
  const handleOpenProcessBomSheet = async () => {
    await guardOpenSpreadsheet("processBomOnly");
  };

  /**
   * 「全シート一括作成」ボタン押下
   * 4シート全てを生成する。
   */
  const handleOpenOtherSheets = async () => {
    await guardOpenSpreadsheet("allSheets");
  };

  /**
   * 確認ダイアログOK押下時: pendingOpenMode に応じて生成実行
   */
  const handleConfirmOpenSpreadsheet = async () => {
    const mode = pendingOpenMode;
    onCloseConfirmDialog();
    setPendingOpenMode(null);
    if (mode) {
      await openSpreadsheetInternal(mode);
    }
  };

  /**
   * 確認ダイアログキャンセル押下時
   */
  const handleCancelOpenSpreadsheet = () => {
    onCloseConfirmDialog();
    setPendingOpenMode(null);
  };

  /**
   * 「データ連携」ボタン押下
   */
  const handleDataLink = async () => {
    if (!activeSpreadsheetId || !bomSheetsData) {
      return;
    }

    try {
      if (cycleTimeUnit === "") {
        toast({
          title: TITLE,
          description: t('features.InvestmentScreening.BomInfoList.message.selectCycleTimeUnit'),
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
        return;
      }

      setIsLoading(true);

      // Googleアカウント認証（念のため）
      if (!await googleAPI.signIn()) {
        throw new Error("signIn error");
      }

      // シート存在チェック（MaterialCostシート有無で読取範囲を切り替え）
      const spreadsheetInfo = await googleAPI.getSpreadSheetInfo(activeSpreadsheetId);
      const sheetTitles: string[] = (spreadsheetInfo.sheets ?? []).map((s: any) => s.properties?.title ?? "");
      const hasMaterialCostSheet = sheetTitles.includes(sheetNames.materialCost);

      // C-2対応: ProcessBOMのみ分岐時に isMaterialCostRow を既存値から復元するためのマップ
      // （MaterialCostシート未生成時は直後の読取処理で復元されないため、事前に複数キー→選択状態を退避）
      // 複数キーでフォールバック可能にする（processCode空、DB保存値と生成値の差分等に対応）
      const prevMaterialRowMap = new Map<string, boolean>();
      // 配列インデックス順の退避（同順照合の最終フォールバック）
      const prevMaterialRowsByIndex: boolean[] = bomSheetsData.processBom.dataRows.map(
        r => r.isMaterialCostRow === true
      );
      for (const row of bomSheetsData.processBom.dataRows) {
        const isMaterial = row.isMaterialCostRow === true;
        // 生成processCode（DB側processCodeが空でもこれで一致可能）
        const generatedCode = (row.modelNo && row.itemNo && row.level)
          ? `DUMMY-${String(row.modelNo).trim()}-${String(row.itemNo).trim()}-${String(row.level).trim()}`
          : "";
        const seq = row.seq ?? "";
        const modelNo = row.modelNo ?? "";
        const itemNo = row.itemNo ?? "";
        const level = row.level ?? "";
        const pc = row.processCode ?? "";
        const candidateKeys = [
          pc,
          generatedCode,
          seq && pc ? `${seq}__${pc}` : "",
          seq && generatedCode ? `${seq}__${generatedCode}` : "",
          // modelNo/processCode が両方空の材料行（BoiseBuffalo等）向けフォールバック
          // seqユニーク制約下で itemNo+level の2段構成により誤マッチを防止
          seq && itemNo && level ? `${seq}__${itemNo}__${level}` : "",
          seq && itemNo ? `${seq}__${itemNo}` : "",
          seq && modelNo && itemNo && level ? `${seq}__${modelNo}__${itemNo}__${level}` : "",
        ].filter(k => !!k);
        // true優先で保存（重複時に true が false に上書きされないようにする）
        for (const k of candidateKeys) {
          if (prevMaterialRowMap.get(k) !== true) {
            prevMaterialRowMap.set(k, isMaterial);
          }
        }
      }

      // 共通列数（A〜F列）
      const commonColumnCount = Object.keys(columnNames.common).length;

      /**
       * CycleTimeを秒に変換する
       * - サイクルタイム単位が「分」の場合：×60して秒に変換
       * - サイクルタイム単位が「秒」の場合：そのまま
       * @param value CycleTime値
       * @returns 秒に変換された値
       */
      const convertCycleTimeToSeconds = (value: any): string => {
        if (value === null || value === undefined || value === "") {
          return "";
        }
        const numValue = Number(value);
        if (isNaN(numValue)) {
          return String(value);
        }
        // 「分」が選択されている場合は×60して秒に変換
        if (cycleTimeUnit === "minute") {
          return String(numValue * 60);
        }
        // 「秒」が選択されている場合はそのまま
        return String(numValue);
      };

      // ■ ProcessBOM(initial) シートからデータ取得（共通項目のみ）
      const processBomData = await googleAPI.spreadSheetGetValues(
        activeSpreadsheetId,
        sheetNames.processBom
      );

      // 共通項目の位置
      const commonHeaderPos: Record<string, number> = {
        [columnNames.common.Seq]: 0,
        [columnNames.common.Level]: 0,
        [columnNames.common.ModelNo]: 0,
        [columnNames.common.ProcessCode]: 0,
        [columnNames.common.ItemNo]: 0,
        [columnNames.common.ItemName]: 0,
      };

      // 項目定義位置設定
      const setPosition = (headerRow: any[], headerPos: Record<string, number>) => {
        const keys = Object.keys(headerPos);

        for (const key of keys) {
          const pos = headerRow.findIndex(heaerName => heaerName === key);
          headerPos[key] = pos;
        }
      }

      const generateProcessCode = (modelNo: string, itemNo: string, level: string) => {
        if (!(modelNo || null) || !(itemNo || null) || !(level || null)) {
          return "";
        }

        // DUMMY-[Model No.]-[Item No.]-[Level]
        return `DUMMY-${(modelNo)?.trim()}-${(itemNo)?.trim()}-${(level)?.trim()}`;
      }

      // ProcessBOM(initial)シートの固有項目位置
      const processBomHeaderPos: Record<string, number> = {
        [columnNames.processBom.IsCustomerSpecifiedParts]: 0,
      };

      if (processBomData.values && processBomData.values.length > 1) {
        // 共通項目位置特定
        setPosition(processBomData.values[1], commonHeaderPos);
        // ProcessBOM固有項目位置特定
        setPosition(processBomData.values[1], processBomHeaderPos);
      }

      if (processBomData.values && processBomData.values.length > 2) {
        // 3行目以降がデータ行（1行目:PartNo、2行目:ヘッダ）
        // 最終行が数式行の場合は除外（Seq.が空の行）
        const dataRows = processBomData.values.slice(2).filter((row: any[]) => row[0] && row[0] !== "");
        bomSheetsData.processBom.dataRows = dataRows.map((row: any[], idx: number) => {
          const isCustomerSpecifiedPartsValue = row[processBomHeaderPos[columnNames.processBom.IsCustomerSpecifiedParts]];

          const modelNo = String(row[commonHeaderPos[columnNames.common.ModelNo]] ?? "");
          const level = String(row[commonHeaderPos[columnNames.common.Level]] ?? "");
          const itemNo = String(row[commonHeaderPos[columnNames.common.ItemNo]] ?? "");
          const rawProcessCode = String(row[commonHeaderPos[columnNames.common.ProcessCode]] ?? "");
          const generatedCode = generateProcessCode(modelNo, itemNo, level);
          const processCode = rawProcessCode || generatedCode;
          const seq = String(row[commonHeaderPos[columnNames.common.Seq]] ?? "");

          // C-2対応: MaterialCostシート未生成時は複数キーでフォールバック照合
          const lookupKeys = [
            rawProcessCode,
            processCode,
            generatedCode,
            seq && rawProcessCode ? `${seq}__${rawProcessCode}` : "",
            seq && processCode ? `${seq}__${processCode}` : "",
            seq && generatedCode ? `${seq}__${generatedCode}` : "",
            // modelNo/processCode が両方空の材料行（BoiseBuffalo等）向けフォールバック
            seq && itemNo && level ? `${seq}__${itemNo}__${level}` : "",
            seq && itemNo ? `${seq}__${itemNo}` : "",
            seq && modelNo && itemNo && level ? `${seq}__${modelNo}__${itemNo}__${level}` : "",
          ].filter(k => !!k);
          let restoredIsMaterial: boolean | undefined;
          for (const k of lookupKeys) {
            const v = prevMaterialRowMap.get(k);
            if (v !== undefined) {
              restoredIsMaterial = v;
              break;
            }
          }
          // 最終フォールバック: 配列インデックスが同一なら既存値を流用（行追加が末尾の場合に機能）
          if (restoredIsMaterial === undefined && idx < prevMaterialRowsByIndex.length) {
            restoredIsMaterial = prevMaterialRowsByIndex[idx];
          }

          return {
            seq,
            level,
            modelNo,
            processCode,
            itemNo,
            itemName: String(row[commonHeaderPos[columnNames.common.ItemName]] ?? ""),
            // MaterialCostシート有: 直後の読取でisMaterialCostRowを復元するため一旦false（既存動作）
            // MaterialCostシート無: 読取処理が走らないため、画面既存値から複数キー+インデックスで復元（C-2対応）
            isMaterialCostRow: hasMaterialCostSheet ? false : (restoredIsMaterial ?? false),
            isCustomerSpecifiedParts: isCustomerSpecifiedPartsValue === true || isCustomerSpecifiedPartsValue === "TRUE" || isCustomerSpecifiedPartsValue === "true",
          } as ProcessBomInitialRow;
        });
      } else {
        // 空にする。
        bomSheetsData.processBom.dataRows = [];

        toast({
          title: TITLE,
          description: t('features.InvestmentScreening.BomInfoList.message.requireProcessBomData', { sheetName: sheetNames.processBom }),
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
      }

      // MaterialCostシート存在時のみ、MaterialCost/Cycletime/Yieldシートを読み取る
      // （未存在時は画面状態を維持し、共通項目コピー処理で必要に応じて再生成される）
      if (hasMaterialCostSheet) {
        // MaterialCostシートの固定列数（共通6列 + 購入通貨 + Usage + Type = 9列）
        // ※ 年月別データは固定列の後から開始
        const materialCostFixedColumnCount = commonColumnCount + 3; // 共通6列 + 購入通貨 + Usage + Type

        // ■ MaterialCost シートからデータ取得
        const materialCostData = await googleAPI.spreadSheetGetValues(
          activeSpreadsheetId,
          sheetNames.materialCost
        );

        // MaterialCostシートの構造:
        // Row 0-3: ヘッダー（U/P(inhouse), U/P(external), Inhouse Ratio, Material Yield の初期値行）
        // Row 4: ヘッダー行（Seq., Level, Model No., Process Code, Item No., Item Name, Type, 年月列...）
        // Row 5以降: データ行
        //   - プロセス行: Seqあり、Type列が空
        //   - 材料費行: Seqあり、Type列が「U/P(inhouse)」、次の3行にU/P(external), Inhouse Ratio, Material Yieldが続く

        const materialCostHeaderRowIndex = 4; // ヘッダー行のインデックス
        const materialCostDataStartRowIndex = 5; // データ開始行のインデックス

        if (materialCostData.values && materialCostData.values.length > materialCostHeaderRowIndex) {
          // 共通項目位置特定
          setPosition(materialCostData.values[materialCostHeaderRowIndex], commonHeaderPos);
        }

        if (materialCostData.values && materialCostData.values.length > materialCostDataStartRowIndex) {
          const headerRow = materialCostData.values[materialCostHeaderRowIndex];
          const yearMonthHeaders = headerRow.slice(materialCostFixedColumnCount).map((header: any, idx: number) => {
            const yearMonth = convertToYearMonth(header);
            const regex = /^\d{4}-(0[1-9]|1[0-2])$/;

            if (!regex.test(yearMonth)) {
              console.warn(`${sheetNames.materialCost} シート - ${googleAPI.columnToLetter(commonColumnCount + idx + 1)} 列 Invalid YearMonth Column : ${yearMonth}`);
            }

            return yearMonth;
          }
          );

          const materialCostHeaderPos: Record<string, number> = {
            [columnNames.materialCost.PurchaseCurrency]: 0,
            [columnNames.materialCost.Usage]: 0,
            [columnNames.materialCost.Type]: 0,
          };

          // 固有項目位置特定
          setPosition(headerRow, materialCostHeaderPos);

          // 全行データを取得（ヘッダー行以降）
          const allRows = materialCostData.values.slice(materialCostDataStartRowIndex);

          // 材料費行のデータを読み取る
          const materialCostRows: MaterialCostRow[] = [];
          // 材料行のキー（seq+processCode）を記録。ProcessBOM途中行削除時などで
          // ProcessBOMとMaterialCostの行順が一致しない場合でも正しく反映するために使用
          const materialRowKeys = new Set<string>();
          let rowIndex = 0;

          while (rowIndex < allRows.length) {
            const row = allRows[rowIndex];

            // Seqが空の行はスキップ（材料費行の後続行など）
            if (!row[0] || row[0] === "") {
              rowIndex++;
              continue;
            }

            // 年月別データ（各項目ごと）
            const unitPriceCombinedMonthly: Record<string, string> = {};
            const unitPriceInhouseMonthly: Record<string, string> = {};
            const unitPriceExternalMonthly: Record<string, string> = {};
            const inhouseRatioMonthly: Record<string, string> = {};
            const materialYieldMonthly: Record<string, string> = {};
            const amountMonthly: Record<string, string> = {};

            // Type列の値を確認
            const typeValue = String(row[materialCostHeaderPos[columnNames.materialCost.Type]] ?? "");
            const isMaterialCostRow = typeValue === columnNames.materialCost.UnitPriceInhouse;

            // 行のキー（seq+processCode）
            const rowSeq = String(row[commonHeaderPos[columnNames.common.Seq]] ?? "");
            const rowProcessCode = String(row[commonHeaderPos[columnNames.common.ProcessCode]] ?? "");
            if (isMaterialCostRow) {
              materialRowKeys.add(`${rowSeq}__${rowProcessCode}`);
            }

            // Usage値を取得（MaterialCostシートの固定列から取得）
            const usage = String(row[materialCostHeaderPos[columnNames.materialCost.Usage]] ?? "");

            if (isMaterialCostRow) {

              // 材料費行の場合、4行にわたるデータを読み取る
              // 現在行: U/P(inhouse)
              // 次の行: U/P(external)
              // その次: Inhouse Ratio
              // その次: Material Yield

              const upInhouseRow = row;
              const upExternalRow = allRows[rowIndex + 1] || [];
              const inhouseRatioRow = allRows[rowIndex + 2] || [];
              const materialYieldRow = allRows[rowIndex + 3] || [];

              yearMonthHeaders.forEach((yearMonth: string, index: number) => {
                const colIndex = materialCostFixedColumnCount + index;

                // U/P(inhouse)
                const upInhouse = String(upInhouseRow[colIndex] ?? "");
                unitPriceInhouseMonthly[yearMonth] = upInhouse;

                // U/P(external)
                const upExternal = String(upExternalRow[colIndex] ?? "");
                unitPriceExternalMonthly[yearMonth] = upExternal;

                // Inhouse Ratio
                const inhouseRatio = String(inhouseRatioRow[colIndex] ?? "");
                inhouseRatioMonthly[yearMonth] = convertPercentToDisplay(inhouseRatio);

                // Material Yield
                const materialYield = String(materialYieldRow[colIndex] ?? "");
                materialYieldMonthly[yearMonth] = convertPercentToDisplay(materialYield);

                // U/P (Combined)を計算: (U/P(inhouse) × Inhouse Ratio + U/P(external) × (1 - Inhouse Ratio)) ÷ Material Yield
                try {
                  const upInhouseVal = upInhouse !== "" && !isNaN(Number(upInhouse)) ? new Decimal(upInhouse) : new Decimal(0);
                  const upExternalVal = upExternal !== "" && !isNaN(Number(upExternal)) ? new Decimal(upExternal) : new Decimal(0);
                  let inhouseRatioVal = inhouseRatio !== "" && !isNaN(Number(inhouseRatio.replace("%", ""))) ? new Decimal(inhouseRatio.replace("%", "")) : new Decimal(0);
                  let materialYieldVal = materialYield !== "" && !isNaN(Number(materialYield.replace("%", ""))) && materialYield !== "0" ? new Decimal(materialYield.replace("%", "")) : new Decimal(1);

                  if (inhouseRatio.endsWith("%")) {
                    inhouseRatioVal = inhouseRatioVal.div(100);
                  }
                  if (materialYield.endsWith("%")) {
                    materialYieldVal = materialYieldVal.div(100);
                  }

                  const upCombined = upInhouseVal.mul(inhouseRatioVal)
                    .plus(upExternalVal.mul(new Decimal(1).minus(inhouseRatioVal)))
                    .div(materialYieldVal);
                  unitPriceCombinedMonthly[yearMonth] = upCombined.toString();

                  // Amount = U/P (Combined) × Usage
                  const usageVal = usage !== "" ? new Decimal(usage) : new Decimal(0);
                  const amount = upCombined.mul(usageVal);
                  amountMonthly[yearMonth] = amount.toString();
                } catch (e) {
                  console.error("U/P Combined calculation error:", e);
                  unitPriceCombinedMonthly[yearMonth] = "";
                  amountMonthly[yearMonth] = "";
                }
              });

              // 材料費行は次の3行をスキップ
              rowIndex += 4;
            } else {
              // プロセス行の場合、年月別データは空
              yearMonthHeaders.forEach((yearMonth: string) => {
                unitPriceCombinedMonthly[yearMonth] = "";
                unitPriceInhouseMonthly[yearMonth] = "";
                unitPriceExternalMonthly[yearMonth] = "";
                inhouseRatioMonthly[yearMonth] = "";
                materialYieldMonthly[yearMonth] = "";
                amountMonthly[yearMonth] = "";
              });

              rowIndex++;
            }

            materialCostRows.push({
              seq: String(row[commonHeaderPos[columnNames.common.Seq]] ?? ""),
              level: String(row[commonHeaderPos[columnNames.common.Level]] ?? ""),
              modelNo: String(row[commonHeaderPos[columnNames.common.ModelNo]] ?? ""),
              processCode: String(row[commonHeaderPos[columnNames.common.ProcessCode]] ?? ""),
              itemNo: String(row[commonHeaderPos[columnNames.common.ItemNo]] ?? ""),
              itemName: String(row[commonHeaderPos[columnNames.common.ItemName]] ?? ""),
              usage: usage,
              purchaseCurrencyId: isMaterialCostRow
                ? (() => {
                  // 材料費行の場合：SpreadSheetの購入通貨列の通貨名からcurrencyIdに変換
                  // SpreadSheetで未設定の場合は空文字列を返す（バリデーションで検知するため）
                  const purchaseCurrencyName = String(row[materialCostHeaderPos[columnNames.materialCost.PurchaseCurrency]] ?? "");
                  if (!purchaseCurrencyName) {
                    return "";
                  }
                  const matchedCurrency = props.currencyList.find(c => c.currencyName === purchaseCurrencyName);
                  return matchedCurrency?.currencyId || "";
                })()
                : null,
              unitPriceCombinedMonthly,
              unitPriceInhouseMonthly,
              unitPriceExternalMonthly,
              inhouseRatioMonthly,
              materialYieldMonthly,
              amountMonthly,
            } as MaterialCostRow);
          }

          bomSheetsData.materialCost.dataRows = materialCostRows;

          // ProcessBOMのisMaterialCostRowをキー一致（seq+processCode）で復元
          // 行順対応ではなくキーで判定することで、ProcessBOM途中行削除・順序変更でも正しく反映
          bomSheetsData.processBom.dataRows.forEach((pbRow) => {
            const key = `${pbRow.seq ?? ""}__${pbRow.processCode ?? ""}`;
            pbRow.isMaterialCostRow = materialRowKeys.has(key);
          });
        } else {
          // 空にする。
          bomSheetsData.materialCost.dataRows = [];
        }

        // ■ Cycletime, Yield シートからデータ取得（共通処理）
        const sheetConfigs: {
          sheetName: string;
          targetKey: "Cycletime" | "Yield";
          isPercentage: boolean;
          isCycleTime: boolean;
        }[] = [
            {
              sheetName: sheetNames.cycletime,
              targetKey: "Cycletime",
              isPercentage: false,
              isCycleTime: true,
            },
            {
              sheetName: sheetNames.yield,
              targetKey: "Yield",
              isPercentage: true,
              isCycleTime: false,
            },
          ];

        for (const config of sheetConfigs) {
          const sheetData = await googleAPI.spreadSheetGetValues(
            activeSpreadsheetId,
            config.sheetName
          );

          if (sheetData.values && sheetData.values.length > 2) {
            // 共通項目位置特定
            setPosition(sheetData.values[2], commonHeaderPos);
          }

          if (sheetData.values && sheetData.values.length > 3) {
            const headerRow = sheetData.values[2];
            // 年月ヘッダーは共通列の直後から開始
            const yearMonthHeaders = headerRow.slice(commonColumnCount).map((header: any, idx: number) => {
              const yearMonth = convertToYearMonth(header)
              const regex = /^\d{4}-(0[1-9]|1[0-2])$/;

              if (!regex.test(yearMonth)) {
                console.warn(`${config.sheetName} シート - ${googleAPI.columnToLetter(commonColumnCount + idx + 1)} 列 Invalid YearMonth Column : ${yearMonth}`);
              }

              return yearMonth;
            });

            const dataRows = sheetData.values.slice(3).filter((row: any[]) => row[0] && row[0] !== "");

            const convertedRows = dataRows.map((row: any[]) => {
              const baseRow: ProcessBomCommonRow = {
                seq: String(row[commonHeaderPos[columnNames.common.Seq]] ?? ""),
                level: String(row[commonHeaderPos[columnNames.common.Level]] ?? ""),
                modelNo: String(row[commonHeaderPos[columnNames.common.ModelNo]] ?? ""),
                processCode: String(row[commonHeaderPos[columnNames.common.ProcessCode]] ?? ""),
                itemNo: String(row[commonHeaderPos[columnNames.common.ItemNo]] ?? ""),
                itemName: String(row[commonHeaderPos[columnNames.common.ItemName]] ?? ""),
              };

              const monthlyData: Record<string, string> = {};
              yearMonthHeaders.forEach((yearMonth: string, index: number) => {
                const value = row[commonColumnCount + index];
                if (config.isPercentage) {
                  monthlyData[yearMonth] = convertPercentToDisplay(value);
                } else if (config.isCycleTime) {
                  monthlyData[yearMonth] = convertCycleTimeToSeconds(value);
                } else {
                  monthlyData[yearMonth] = String(value ?? "");
                }
              });

              return {
                ...baseRow,
                monthlyData,
              };
            });

            if (config.targetKey === "Cycletime") {
              bomSheetsData.cycletime.dataRows = convertedRows as CycletimeRow[];
            } else if (config.targetKey === "Yield") {
              bomSheetsData.yield.dataRows = convertedRows as YieldRow[];
            }
          } else {
            // 空にする。
            if (config.targetKey === "Cycletime") {
              bomSheetsData.cycletime.dataRows = [];
            } else if (config.targetKey === "Yield") {
              bomSheetsData.yield.dataRows = [];
            }
          }
        }
      } // end if (hasMaterialCostSheet)

      // ■ ProcessBOM(initial)シートの共通項目を他シートにコピー
      // ProcessBOM(initial)シートにデータがあり、他シートが未入力の場合にコピー
      if (bomSheetsData.processBom.dataRows.length > 0) {
        const processBomRows = bomSheetsData.processBom.dataRows;

        // 空の年月別データ生成ヘルパー
        const buildEmptyMonthlyData = (): Record<string, string> => {
          const m: Record<string, string> = {};
          yearMonthList.forEach((ym) => { m[ym] = ""; });
          return m;
        };

        // 両経路共通の差分同期（案1-b）:
        // 既存データ（DB由来・前回連携値・スプレッドシート読取値）を維持しつつ
        // ProcessBOMの行差分だけ追加/削除する。
        // マッチキーは seq + processCode（両方一致で同一行と判定）。
        // ProcessBOM単独作成経路: 画面の materialCost/cycletime/yield を prev として使用
        // 全シート一括作成経路: スプレッドシートから読み取った最新の materialCost/cycletime/yield を prev として使用
        const makeKey = (seq?: string, processCode?: string) => `${seq ?? ""}__${processCode ?? ""}`;

        const prevMaterialCostMap = new Map(
          bomSheetsData.materialCost.dataRows.map((r) => [makeKey(r.seq, r.processCode), r])
        );
        const prevCycletimeMap = new Map(
          bomSheetsData.cycletime.dataRows.map((r) => [makeKey(r.seq, r.processCode), r])
        );
        const prevYieldMap = new Map(
          bomSheetsData.yield.dataRows.map((r) => [makeKey(r.seq, r.processCode), r])
        );

        bomSheetsData.materialCost.dataRows = processBomRows.map((row) => {
          const key = makeKey(row.seq, row.processCode);
          const prev = prevMaterialCostMap.get(key);
          if (prev) {
            // 既存行が見つかったら年月別データ等は維持、共通項目のみProcessBOM側で上書き
            return {
              ...prev,
              seq: row.seq,
              level: row.level,
              modelNo: row.modelNo,
              processCode: row.processCode,
              itemNo: row.itemNo,
              itemName: row.itemName,
            } as MaterialCostRow;
          }
          // 新規追加行: 空の年月別データで作成
          return {
            seq: row.seq,
            level: row.level,
            modelNo: row.modelNo,
            processCode: row.processCode,
            itemNo: row.itemNo,
            itemName: row.itemName,
            purchaseCurrencyId: null,
            unitPriceCombinedMonthly: buildEmptyMonthlyData(),
            unitPriceInhouseMonthly: buildEmptyMonthlyData(),
            unitPriceExternalMonthly: buildEmptyMonthlyData(),
            inhouseRatioMonthly: buildEmptyMonthlyData(),
            materialYieldMonthly: buildEmptyMonthlyData(),
            amountMonthly: buildEmptyMonthlyData(),
          } as MaterialCostRow;
        });

        bomSheetsData.cycletime.dataRows = processBomRows.map((row) => {
          const key = makeKey(row.seq, row.processCode);
          const prev = prevCycletimeMap.get(key);
          if (prev) {
            return {
              ...prev,
              seq: row.seq,
              level: row.level,
              modelNo: row.modelNo,
              processCode: row.processCode,
              itemNo: row.itemNo,
              itemName: row.itemName,
            } as CycletimeRow;
          }
          return {
            seq: row.seq,
            level: row.level,
            modelNo: row.modelNo,
            processCode: row.processCode,
            itemNo: row.itemNo,
            itemName: row.itemName,
            monthlyData: buildEmptyMonthlyData(),
          } as CycletimeRow;
        });

        bomSheetsData.yield.dataRows = processBomRows.map((row) => {
          const key = makeKey(row.seq, row.processCode);
          const prev = prevYieldMap.get(key);
          if (prev) {
            return {
              ...prev,
              seq: row.seq,
              level: row.level,
              modelNo: row.modelNo,
              processCode: row.processCode,
              itemNo: row.itemNo,
              itemName: row.itemName,
            } as YieldRow;
          }
          return {
            seq: row.seq,
            level: row.level,
            modelNo: row.modelNo,
            processCode: row.processCode,
            itemNo: row.itemNo,
            itemName: row.itemName,
            monthlyData: buildEmptyMonthlyData(),
          } as YieldRow;
        });
      }

      // バリデーション実行
      const errors = validateAllSheets(bomSheetsData);
      setValidationErrors(errors);

      // データ連携成功 = 未連携リスクなしの状態
      setIsDataLinked(true);

      // エラー件数を計算（各行のエラーフィールド数を合計）
      const errorCount = Object.values(errors).reduce((total, sheetErrors) => {
        return total + (Object.values(sheetErrors) as RowErrors[]).reduce((rowTotal, rowErrors) => {
          return rowTotal + Object.keys(rowErrors).length;
        }, 0);
      }, 0);

      if (errorCount > 0) {
        toast({
          title: TITLE,
          description: t('features.InvestmentScreening.BomInfoList.message.dataLinkCompletedWithErrors', { errorCount }),
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
      } else {
        toast({
          title: TITLE,
          description: t('features.InvestmentScreening.BomInfoList.message.dataLinkCompleted'),
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      }

    } catch (error) {
      console.error("Data Link Error.", error);
      toast({
        title: TITLE,
        description: t('features.InvestmentScreening.BomInfoList.message.dataLinkFailed'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * 「保存」ボタン押下
   */
  const handleSave = async () => {
    // DB保存(r_ceas_bom)
    if (await putBom(bomSheetsData) !== "success") {
      return;
    }

    // 材料費とプロセスフロー情報の保存処理
    const { resultMaterialCostData, resultProcessData } = await props.onSave(bomSheetsData);

    toast({
      title: TITLE,
      description: t('features.InvestmentScreening.BomInfoList.message.bomSaveSuccess'),
      status: 'success',
      duration: 3000,
      isClosable: true,
    });

    if (!resultMaterialCostData) {
      toast({
        title: TITLE,
        description: t('features.InvestmentScreening.BomInfoList.message.materialCostSaveFailed'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }

    if (!resultProcessData) {
      toast({
        title: TITLE,
        description: t('features.InvestmentScreening.BomInfoList.message.flowSaveFailed'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }

    // BOM情報再取得
    const bomData = await getBomByProjectId(props.projectId, props.partNo!) as BomData[];

    // BOM情報 ⇒ 全シートデータの統合型 変換
    const tempBomSheetsData = convertBomSheetsData(bomData);
    setBomSheetsData(tempBomSheetsData);

    if (bomData.length > 0) {
      // 更新日時
      setUpdateDate(bomData[0].updateDate);
    }

    if (bomData.length > 0) {
      // 既存データありの場合、[秒]
      setCycleTimeUnit("second");
    }
  };

  /**
   * 「閉じる」ボタン押下
   */
  const handleClose = () => {
    props.onClose();
  };

  /**
   * ProcessBOM(initial)情報更新
   * @param dataRowIdx 行インデックス
   * @param propertyName プロパティ名
   * @param value 
   */
  const changeProcessBom = (
    dataRowIdx: number,
    propertyName: string,
    value: any
  ) => {
    setBomSheetsData((prev) => {
      // 深いコピーを作成
      const newData = { ...prev };
      const newRows = [...prev.processBom.dataRows];

      if (newRows[dataRowIdx]) {
        newRows[dataRowIdx] = {
          ...newRows[dataRowIdx],
          [propertyName]: value,
        };
      }
      newData.processBom = { dataRows: newRows };

      return newData;
    });
  }

  /**
   * 全シートデータの統合型に変換
   * @param bomData BOM情報
   */
  const convertBomSheetsData = (bomData: BomData[]) => {

    const bomSheetsData: BomSheetsData = {
      processBom: {
        dataRows: [],
      },
      materialCost: {
        dataRows: [],
      },
      cycletime: {
        dataRows: [],
      },
      yield: {
        dataRows: [],
      }
    };

    // 年月管理データ変換
    const convertMonthly = (monthlyJson: string | null | undefined) => {
      try {
        if (!monthlyJson || monthlyJson === "") {
          return {};
        }

        return JSON.parse(monthlyJson) as Record<string, string>;
      } catch (error) {
        console.log(`convertMonthly error`, monthlyJson);
        return {};
      }
    }

    // データ変換ループ
    for (const row of bomData) {
      // ■ 共通
      const commonRow = {
        seq: row.seq || "",
        level: row.level || "",
        modelNo: row.modelNo || "",
        processCode: row.processCode || "",
        itemNo: row.itemNo || "",
        itemName: row.itemName || "",
      };

      const unitPriceCombinedMonthly = convertMonthly(row.unitPriceCombinedMonthly);
      const unitPriceInhouseMonthly = convertMonthly(row.unitPriceInhouseMonthly);
      const unitPriceExternalMonthly = convertMonthly(row.unitPriceExternalMonthly);
      const inhouseRatioMonthly = convertMonthly(row.inhouseRatioMonthly);
      const materialYieldMonthly = convertMonthly(row.materialYieldMonthly);
      const amountMonthly = convertMonthly(row.amountMonthly);

      // 材料費行判定(１つでもデータがあれば、材料費行)
      const isMaterialCostRow = [
        unitPriceCombinedMonthly,
        unitPriceInhouseMonthly,
        unitPriceExternalMonthly,
        inhouseRatioMonthly,
        materialYieldMonthly,
        amountMonthly,
      ].some(monthlyData => Object.values(monthlyData).some(v => v !== ""));


      // ■ ProcessBOM 固有
      const processBomRow: ProcessBomInitialRow = {
        ...commonRow,
        isMaterialCostRow: isMaterialCostRow,
        isCustomerSpecifiedParts: row.isCustomerSpecifiedParts === "true",
      };
      bomSheetsData.processBom.dataRows.push(processBomRow);

      // ■ MaterialCost 固有
      const materialCostRow: MaterialCostRow = {
        ...commonRow,
        usage: row.usage || "",
        purchaseCurrencyId: row.purchaseCurrencyId || "",
        unitPriceCombinedMonthly: unitPriceCombinedMonthly,
        unitPriceInhouseMonthly: unitPriceInhouseMonthly,
        unitPriceExternalMonthly: unitPriceExternalMonthly,
        inhouseRatioMonthly: inhouseRatioMonthly,
        materialYieldMonthly: materialYieldMonthly,
        amountMonthly: amountMonthly,
      };
      bomSheetsData.materialCost.dataRows.push(materialCostRow);

      // ■ Cycletime 固有
      const cycletimeRow: CycletimeRow = {
        ...commonRow,
        monthlyData: convertMonthly(row.cycleTimeMonthly),
      };
      bomSheetsData.cycletime.dataRows.push(cycletimeRow);

      // ■ Yield 固有
      const yieldRow: YieldRow = {
        ...commonRow,
        monthlyData: convertMonthly(row.yieldMonthly),
      };
      bomSheetsData.yield.dataRows.push(yieldRow);
    }

    return bomSheetsData;
  }

  /**
   * BOM情報取得
   * @param projectId プロジェクトID
   * @param partNo パーツNo
   * @returns BOM情報
   */
  const getBomByProjectId = async (projectId: string, partNo: string) => {
    const functionName = `getBomByProjectId`;
    const params = `(projectId : ${projectId}, partNo : ${partNo})`;

    try {
      console.log(`${functionName}${params} START`);

      const payload = {
        query: `
            query getBomByProjectId($projectId: String, $partNo: String, $reviewDate: String) {
              getBomByProjectId(projectId: $projectId, partNo: $partNo, reviewDate: $reviewDate) {
                projectId
                partNo
                seq
                level
                modelNo
                processCode
                itemNo
                itemName
                usage
                isCustomerSpecifiedParts
                purchaseCurrencyId
                unitPriceCombinedMonthly
                unitPriceInhouseMonthly
                unitPriceExternalMonthly
                inhouseRatioMonthly
                materialYieldMonthly
                amountMonthly
                cycleTimeMonthly
                yieldMonthly
                updateDate
              }
            }
        `,
        variables: { projectId, partNo, ...(reviewDate && { reviewDate }) }
      };

      // DB情報取得
      const response = await WebAPI.request(payload);
      console.log(`${functionName} response = `, response);

      if (!(response?.data?.[functionName])) {
        // データ取得失敗の場合
        toast({
          title: TITLE,
          description: t('features.InvestmentScreening.BomInfoList.message.bomFetchFailed'),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });

        return [];
      }

      // 返却
      return response.data[functionName];

    } catch (error) {
      console.log(`${functionName} ERROR.`, error);
      toast({
        title: TITLE,
        description: t('features.InvestmentScreening.BomInfoList.message.dataFetchFailed'),
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      console.log(`${functionName} END`);
    }
  }

  /**
   * 全シートデータDB保存処理
   * @param bomSheetsData 全シートデータ
   * @returns 
   */
  const putBom = async (bomSheetsData: BomSheetsData) => {
    const functionName = "putBom";
    const params = `bomSheetsData = ${JSON.stringify(bomSheetsData)}`;

    try {
      console.log(`${functionName}${params} START`);

      // SpreadSheetデータ
      const bomData = bomSheetsData.processBom.dataRows.map((row, idx) => {

        const unitPriceCombinedMonthly = bomSheetsData.materialCost.dataRows[idx].unitPriceCombinedMonthly;
        const unitPriceInhouseMonthly = bomSheetsData.materialCost.dataRows[idx].unitPriceInhouseMonthly;
        const unitPriceExternalMonthly = bomSheetsData.materialCost.dataRows[idx].unitPriceExternalMonthly;
        const inhouseRatioMonthly = bomSheetsData.materialCost.dataRows[idx].inhouseRatioMonthly;
        const materialYieldMonthly = bomSheetsData.materialCost.dataRows[idx].materialYieldMonthly;
        const amountMonthly = bomSheetsData.materialCost.dataRows[idx].amountMonthly;

        if (!row.isMaterialCostRow) {
          // 材料行でない行は明示的に初期化
          yearMonthList.forEach((yearMonth) => {
            unitPriceCombinedMonthly[yearMonth] = "";
            unitPriceInhouseMonthly[yearMonth] = "";
            unitPriceExternalMonthly[yearMonth] = "";
            inhouseRatioMonthly[yearMonth] = "";
            materialYieldMonthly[yearMonth] = "";
            amountMonthly[yearMonth] = "";
          });
        }

        return {
          seq: row.seq,
          level: row.level,
          modelNo: row.modelNo,
          processCode: row.processCode,
          itemNo: row.itemNo,
          itemName: row.itemName,
          usage: bomSheetsData.materialCost.dataRows[idx].usage,
          isCustomerSpecifiedParts: row.isCustomerSpecifiedParts ? "true" : "false",
          purchaseCurrencyId: bomSheetsData.materialCost.dataRows[idx].purchaseCurrencyId,
          unitPriceCombinedMonthly: JSON.stringify(unitPriceCombinedMonthly),
          unitPriceInhouseMonthly: JSON.stringify(unitPriceInhouseMonthly),
          unitPriceExternalMonthly: JSON.stringify(unitPriceExternalMonthly),
          inhouseRatioMonthly: JSON.stringify(inhouseRatioMonthly),
          materialYieldMonthly: JSON.stringify(materialYieldMonthly),
          amountMonthly: JSON.stringify(amountMonthly),
          cycleTimeMonthly: JSON.stringify(bomSheetsData.cycletime.dataRows[idx].monthlyData || {}),
          yieldMonthly: JSON.stringify(bomSheetsData.yield.dataRows[idx].monthlyData || {}),
        };
      });

      // パラメータ
      const input = {
        projectId: props.projectId,
        partNo: props.partNo,
        updateDate: updateDate,
        bomData: bomData,
        ...(reviewDate && { reviewDate }),
      };

      // AppSync
      const result = await WebAPI.request({
        query: `
            mutation putBom($input: BomInput!) {
              putBom(input: $input) {status, message}
            }
          `,
        variables: {
          input: input,
        }
      });

      console.log(`putBom result = `, result);

      if (result?.data?.putBom?.status === 'expired') {
        toast({
          title: TITLE,
          description: Messages.expiredMessage,
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
        console.warn(`${functionName} Conflict error. putBom.`);
        return;
      } else if (result?.data?.putBom?.status !== "success") {
        toast({
          title: TITLE,
          description: t(`${bl}.message.bomSaveFailed`),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        console.error(`${functionName} Failed to call API putBom.`);
        return;
      }

      return result?.data?.putBom?.status;

    } catch (error) {
      console.log(`${functionName} ERROR.`, error);
      toast({
        title: TITLE,
        description: t('features.InvestmentScreening.BomInfoList.message.dataRegisterFailed'),
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return null;
    } finally {
      console.log(`${functionName} END`);
    }
  }

  /**
  * パーセント値を表示用に変換（×100）
  * @param value 値（0.xx形式 または "xx%" 形式）
  * @returns 表示用値（xx形式、%記号なし）
  */
  const convertPercentToDisplay = (value: any): string => {
    if (value === null || value === undefined || value === "") {
      return "";
    }

    // 文字列の場合、%記号を除去
    let strValue = String(value);
    if (strValue.includes("%")) {
      // "95%" → "95" のように%を除去
      strValue = strValue.replace(/%/g, "").trim();
      // 既に%表示形式の場合は×100不要
      const numValue = Number(strValue);
      if (isNaN(numValue)) {
        return strValue;
      }
      return String(numValue);
    }

    // 小数形式の場合は×100
    const numValue = Number(strValue);
    if (isNaN(numValue)) {
      return strValue;
    }
    return String(numValue * 100);
  };

  /**
   * 日付値をyyyy-mm形式に変換
   * @param value 日付値（yyyy/mm/dd, yyyy-mm-dd, シリアル値など）
   * @returns yyyy-mm形式の文字列
   */
  const convertToYearMonth = (value: any): string => {
    if (value === null || value === undefined || value === "") {
      return "";
    }

    // 既にyyyy-mm形式の場合
    if (typeof value === "string" && /^\d{4}-\d{2}$/.test(value)) {
      return value;
    }

    // dayjsでパース
    const parsed = dayjs(value);
    if (parsed.isValid()) {
      return parsed.format("YYYY-MM");
    }

    // シリアル値の場合（Excelの日付シリアル値）
    if (typeof value === "number") {
      // Excelシリアル値を日付に変換（1900年1月1日を基準）
      const excelEpoch = new Date(1899, 11, 30);
      const date = new Date(excelEpoch.getTime() + value * 24 * 60 * 60 * 1000);
      return dayjs(date).format("YYYY-MM");
    }

    return String(value);
  };

  /**
   * 数値かどうかをチェック（空文字は許可）
   * @param value チェック対象値
   * @returns 数値の場合true
   */
  const isValidNumber = (value: string | undefined): boolean => {
    if (value === null || value === undefined || value === "") {
      return true; // 空は許可
    }
    return !isNaN(Number(value));
  };

  /**
   * 必須チェック
   * @param value チェック対象値
   * @returns 値がある場合true
   */
  const isRequired = (value: string | undefined | null): boolean => {
    return value !== null && value !== undefined && value !== "";
  };

  /**
   * パーセンテージ範囲チェック（0〜100%）
   * @param value チェック対象値
   * @returns 0〜100の範囲内の場合true、空の場合もtrue
   */
  const isValidPercentage = (value: string | undefined): boolean => {
    if (value === null || value === undefined || value === "") {
      return true; // 空は許可
    }
    const num = Number(value);
    if (isNaN(num)) return false;
    return num >= 0 && num <= 100;
  };

  /**
   * level文字列を数値に変換
   * - "-" → 0
   * - "*01" → 1, "**02" → 2, "***03" → 3（*の数でレベル判定）
   * - "1", "2", "3" のような単純な数字も許容
   * @param level レベル文字列
   * @returns レベル数値（不正なフォーマットの場合は-1）
   */
  const getLevelNumber = (level: string | undefined): number => {
    if (!level || level === "-") return 0;

    // "*01" → 1, "**02" → 2, "***03" → 3 のパターン（*の数でレベル判定）
    const asteriskMatch = level.match(/^(\*+)\d+$/);
    if (asteriskMatch) {
      return asteriskMatch[1].length; // *の数をカウント
    }

    // 単純な数字 "1", "2", "3" のパターン
    const num = Number(level);
    if (!isNaN(num) && num >= 1) {
      return num;
    }

    return -1; // 不正なフォーマット
  };

  /**
   * 全シートのバリデーションを実行
   * @param data プレビューデータ
   * @returns バリデーションエラー
   */
  const validateAllSheets = (data: BomSheetsData): ValidationErrors => {
    const errors: ValidationErrors = {
      processBom: {},
      materialCost: {},
      cycletime: {},
      yield: {},
    };

    // バリデーションメッセージ定数
    const MSG = {
      required: (label: string) => t('features.InvestmentScreening.BomInfoList.validation.required', { label }),
      numeric: (label: string) => t('features.InvestmentScreening.BomInfoList.validation.numeric', { label }),
      percentage: (label: string) => t('features.InvestmentScreening.BomInfoList.validation.percentage', { label }),
      duplicate: (label: string, value: string) => t('features.InvestmentScreening.BomInfoList.validation.duplicate', { label, value }),
      mismatch: (label: string, value: string) => t('features.InvestmentScreening.BomInfoList.validation.mismatch', { label, value }),
      levelFormat: (label: string) => t('features.InvestmentScreening.BomInfoList.validation.levelFormat', { label }),
      levelFirst: (label: string) => t('features.InvestmentScreening.BomInfoList.validation.levelFirst', { label }),
      levelOnlyFirst: (label: string) => t('features.InvestmentScreening.BomInfoList.validation.levelOnlyFirst', { label }),
      levelTransition: (label: string, prev: number, current: number) => t('features.InvestmentScreening.BomInfoList.validation.levelTransition', { label, prev, current }),
      cycleTimeProcessCode: (label: string) => t('features.InvestmentScreening.BomInfoList.validation.cycleTimeProcessCode', { label }),
      notZero: (label: string) => t('features.InvestmentScreening.BomInfoList.validation.notZero', { label }),
    };

    // Cycletimeシートの年月データが入力されている行のインデックスを収集
    // この行については全シートでprocessCodeが必須となる
    const rowsRequiringProcessCode = new Set<number>();
    data.cycletime.dataRows.forEach((row, index) => {
      // monthlyDataに値がある行をチェック
      const hasMonthlyData = Object.values(row.monthlyData || {}).some(value => isRequired(value));
      if (hasMonthlyData) {
        rowsRequiringProcessCode.add(index);
      }
    });

    // ProcessBOM(initial)シートの共通項目を参照マップとして保持
    // 他シートとの比較に使用
    const processBomInitialRows = data.processBom.dataRows;

    // ProcessBOM(initial)シートのseq重複チェック用
    const seqSet = new Set<string>();
    const duplicateSeqs = new Set<string>();
    data.processBom.dataRows.forEach((row) => {
      if (row.seq) {
        if (seqSet.has(row.seq)) {
          duplicateSeqs.add(row.seq);
        }
        seqSet.add(row.seq);
      }
    });

    // ■ ProcessBOM(initial)シートのバリデーション（共通項目のみ）
    data.processBom.dataRows.forEach((row, index) => {
      const rowErrors: RowErrors = {};

      // 共通列の必須チェック
      // seq: 必須 + 数値 + ユニークチェック
      if (!isRequired(row.seq)) {
        rowErrors.seq = { message: MSG.required(columnNames.common.Seq) };
      } else if (!isValidNumber(row.seq)) {
        rowErrors.seq = { message: MSG.numeric(columnNames.common.Seq) };
      } else if (duplicateSeqs.has(row.seq)) {
        rowErrors.seq = { message: MSG.duplicate(columnNames.common.Seq, row.seq) };
      }

      // level: 必須 + 階層チェック
      if (!isRequired(row.level)) {
        rowErrors.level = { message: MSG.required(columnNames.common.Level) };
      } else {
        const currentLevel = getLevelNumber(row.level);
        if (currentLevel === -1) {
          rowErrors.level = { message: MSG.levelFormat(columnNames.common.Level) };
        } else if (index === 0 && currentLevel !== 0) {
          rowErrors.level = { message: MSG.levelFirst(columnNames.common.Level) };
        } else if (index > 0) {
          if (currentLevel === 0) {
            rowErrors.level = { message: MSG.levelOnlyFirst(columnNames.common.Level) };
          } else {
            const prevLevel = getLevelNumber(data.processBom.dataRows[index - 1].level);
            if (currentLevel > prevLevel + 1) {
              rowErrors.level = { message: MSG.levelTransition(columnNames.common.Level, prevLevel, currentLevel) };
            }
          }
        }
      }
      if (!isRequired(row.itemNo)) {
        rowErrors.itemNo = { message: MSG.required(columnNames.common.ItemNo) };
      }
      if (!isRequired(row.itemName)) {
        rowErrors.itemName = { message: MSG.required(columnNames.common.ItemName) };
      }
      // CycletimeシートのCycleTimeが入力されている行の場合、processCodeは必須
      if (rowsRequiringProcessCode.has(index) && !isRequired(row.processCode)) {
        rowErrors.processCode = { message: MSG.cycleTimeProcessCode(columnNames.cycletime.CycleTime) };
      }

      if (Object.keys(rowErrors).length > 0) {
        errors.processBom[index] = rowErrors;
      }
    });

    // ■ MaterialCostシートのバリデーション
    data.materialCost.dataRows.forEach((row, index) => {
      const rowErrors: RowErrors = {};

      // ProcessBOM(initial)側の対応行・材料費行判定（材料費行の場合のみ、以下の項目が必須）
      const initialRow = processBomInitialRows[index];
      const isMaterialCostRow = initialRow?.isMaterialCostRow;

      // Usageの必須 + 数値チェック（材料費行のみ必須）
      if (isMaterialCostRow && !isRequired(row.usage)) {
        rowErrors.usage = { message: MSG.required(columnNames.materialCost.Usage) };
      } else if (!isValidNumber(row.usage)) {
        rowErrors.usage = { message: MSG.numeric(columnNames.materialCost.Usage) };
      }

      // ProcessBOM(initial)との共通項目不一致チェック
      if (initialRow) {
        if (row.seq !== initialRow.seq) {
          rowErrors.seq = { message: MSG.mismatch(columnNames.common.Seq, initialRow.seq || "") };
        }
        if (row.level !== initialRow.level) {
          rowErrors.level = { message: MSG.mismatch(columnNames.common.Level, initialRow.level || "") };
        }
        if (row.modelNo !== initialRow.modelNo) {
          rowErrors.modelNo = { message: MSG.mismatch(columnNames.common.ModelNo, initialRow.modelNo || "") };
        }
        if (row.processCode !== initialRow.processCode) {
          rowErrors.processCode = { message: MSG.mismatch(columnNames.common.ProcessCode, initialRow.processCode || "") };
        }
        if (row.itemNo !== initialRow.itemNo) {
          rowErrors.itemNo = { message: MSG.mismatch(columnNames.common.ItemNo, initialRow.itemNo || "") };
        }
        if (row.itemName !== initialRow.itemName) {
          rowErrors.itemName = { message: MSG.mismatch(columnNames.common.ItemName, initialRow.itemName || "") };
        }
      } else {
        // ProcessBOM(initial)に対応する行がない場合
        if (!isRequired(row.seq)) {
          rowErrors.seq = { message: MSG.required(columnNames.common.Seq) };
        }
        if (!isRequired(row.level)) {
          rowErrors.level = { message: MSG.required(columnNames.common.Level) };
        }
        if (!isRequired(row.itemNo)) {
          rowErrors.itemNo = { message: MSG.required(columnNames.common.ItemNo) };
        }
        if (!isRequired(row.itemName)) {
          rowErrors.itemName = { message: MSG.required(columnNames.common.ItemName) };
        }
        if (rowsRequiringProcessCode.has(index) && !isRequired(row.processCode)) {
          rowErrors.processCode = { message: MSG.cycleTimeProcessCode(`Cycletimeシートの${columnNames.cycletime.CycleTime}`) };
        }
      }

      // 年月データの必須（材料費行のみ）+ 数値チェック（各項目ごと）
      Object.entries(row.unitPriceCombinedMonthly || {}).forEach(([yearMonth, value]) => {
        if (isMaterialCostRow && !isRequired(value)) {
          rowErrors[`unitPriceCombinedMonthly.${yearMonth}`] = { message: MSG.required(`${columnNames.materialCost.UnitPriceCombined}(${yearMonth})`) };
        } else if (!isValidNumber(value)) {
          rowErrors[`unitPriceCombinedMonthly.${yearMonth}`] = { message: MSG.numeric(`${columnNames.materialCost.UnitPriceCombined}(${yearMonth})`) };
        }
      });
      Object.entries(row.unitPriceInhouseMonthly || {}).forEach(([yearMonth, value]) => {
        if (isMaterialCostRow && !isRequired(value)) {
          rowErrors[`unitPriceInhouseMonthly.${yearMonth}`] = { message: MSG.required(`${columnNames.materialCost.UnitPriceInhouse}(${yearMonth})`) };
        } else if (!isValidNumber(value)) {
          rowErrors[`unitPriceInhouseMonthly.${yearMonth}`] = { message: MSG.numeric(`${columnNames.materialCost.UnitPriceInhouse}(${yearMonth})`) };
        }
      });
      Object.entries(row.unitPriceExternalMonthly || {}).forEach(([yearMonth, value]) => {
        if (isMaterialCostRow && !isRequired(value)) {
          rowErrors[`unitPriceExternalMonthly.${yearMonth}`] = { message: MSG.required(`${columnNames.materialCost.UnitPriceExternal}(${yearMonth})`) };
        } else if (!isValidNumber(value)) {
          rowErrors[`unitPriceExternalMonthly.${yearMonth}`] = { message: MSG.numeric(`${columnNames.materialCost.UnitPriceExternal}(${yearMonth})`) };
        }
      });
      // inhouseRatioMonthly: 必須（材料費行のみ）+ 数値 + パーセンテージ範囲チェック
      Object.entries(row.inhouseRatioMonthly || {}).forEach(([yearMonth, value]) => {
        if (isMaterialCostRow && !isRequired(value)) {
          rowErrors[`inhouseRatioMonthly.${yearMonth}`] = { message: MSG.required(`${columnNames.materialCost.InhouseRatio}(${yearMonth})`) };
        } else if (!isValidNumber(value)) {
          rowErrors[`inhouseRatioMonthly.${yearMonth}`] = { message: MSG.numeric(`${columnNames.materialCost.InhouseRatio}(${yearMonth})`) };
        } else if (!isValidPercentage(value)) {
          rowErrors[`inhouseRatioMonthly.${yearMonth}`] = { message: MSG.percentage(`${columnNames.materialCost.InhouseRatio}(${yearMonth})`) };
        }
      });
      // materialYieldMonthly: 必須（材料費行のみ）+ 数値 + パーセンテージ範囲チェック + 0チェック
      Object.entries(row.materialYieldMonthly || {}).forEach(([yearMonth, value]) => {
        if (isMaterialCostRow && !isRequired(value)) {
          rowErrors[`materialYieldMonthly.${yearMonth}`] = { message: MSG.required(`${columnNames.materialCost.MaterialYield}(${yearMonth})`) };
        } else if (!isValidNumber(value)) {
          rowErrors[`materialYieldMonthly.${yearMonth}`] = { message: MSG.numeric(`${columnNames.materialCost.MaterialYield}(${yearMonth})`) };
        } else if (!isValidPercentage(value)) {
          rowErrors[`materialYieldMonthly.${yearMonth}`] = { message: MSG.percentage(`${columnNames.materialCost.MaterialYield}(${yearMonth})`) };
        } else if (parseFloat(value) === 0) {
          rowErrors[`materialYieldMonthly.${yearMonth}`] = { message: MSG.notZero(`${columnNames.materialCost.MaterialYield}(${yearMonth})`) };
        }
      });
      Object.entries(row.amountMonthly || {}).forEach(([yearMonth, value]) => {
        if (isMaterialCostRow && !isRequired(value)) {
          rowErrors[`amountMonthly.${yearMonth}`] = { message: MSG.required(`${columnNames.materialCost.Amount}(${yearMonth})`) };
        } else if (!isValidNumber(value)) {
          rowErrors[`amountMonthly.${yearMonth}`] = { message: MSG.numeric(`${columnNames.materialCost.Amount}(${yearMonth})`) };
        }
      });

      // 購入通貨の必須チェック（材料費行の場合のみ）
      if (isMaterialCostRow && !isRequired(row.purchaseCurrencyId)) {
        rowErrors.purchaseCurrencyId = { message: MSG.required(columnNames.materialCost.PurchaseCurrency) };
      }

      if (Object.keys(rowErrors).length > 0) {
        errors.materialCost[index] = rowErrors;
      }
    });

    // □ Cycletime, Yieldシートの共通バリデーション
    const validateSheetWithMonthlyData = <T extends ProcessBomCommonRow & { monthlyData: Record<string, string> }>(
      dataRows: T[],
      sheetKey: "cycletime" | "yield",
      isPercentageField?: boolean // パーセンテージ範囲チェックが必要かどうか
    ) => {
      dataRows.forEach((row, index) => {
        const rowErrors: RowErrors = {};

        // ProcessBOM(initial)との共通項目不一致チェック
        const initialRow = processBomInitialRows[index];
        if (initialRow) {
          // Seq.の不一致チェック
          if (row.seq !== initialRow.seq) {
            rowErrors.seq = { message: MSG.mismatch(columnNames.common.Seq, initialRow.seq || "") };
          }
          // Levelの不一致チェック
          if (row.level !== initialRow.level) {
            rowErrors.level = { message: MSG.mismatch(columnNames.common.Level, initialRow.level || "") };
          }
          // Model No.の不一致チェック
          if (row.modelNo !== initialRow.modelNo) {
            rowErrors.modelNo = { message: MSG.mismatch(columnNames.common.ModelNo, initialRow.modelNo || "") };
          }
          // Process Codeの不一致チェック
          if (row.processCode !== initialRow.processCode) {
            rowErrors.processCode = { message: MSG.mismatch(columnNames.common.ProcessCode, initialRow.processCode || "") };
          }
          // Item No.の不一致チェック
          if (row.itemNo !== initialRow.itemNo) {
            rowErrors.itemNo = { message: MSG.mismatch(columnNames.common.ItemNo, initialRow.itemNo || "") };
          }
          // Item Nameの不一致チェック
          if (row.itemName !== initialRow.itemName) {
            rowErrors.itemName = { message: MSG.mismatch(columnNames.common.ItemName, initialRow.itemName || "") };
          }
        } else {
          // ProcessBOM(initial)に対応する行がない場合のチェック
          // 共通列の必須チェック
          if (!isRequired(row.seq)) {
            rowErrors.seq = { message: MSG.required(columnNames.common.Seq) };
          }
          if (!isRequired(row.level)) {
            rowErrors.level = { message: MSG.required(columnNames.common.Level) };
          }
          if (!isRequired(row.itemNo)) {
            rowErrors.itemNo = { message: MSG.required(columnNames.common.ItemNo) };
          }
          if (!isRequired(row.itemName)) {
            rowErrors.itemName = { message: MSG.required(columnNames.common.ItemName) };
          }
          // Cycletimeシートの年月データが入力されている行の場合、processCodeは必須
          if (rowsRequiringProcessCode.has(index) && !isRequired(row.processCode)) {
            rowErrors.processCode = { message: MSG.cycleTimeProcessCode(`Cycletimeシートの年月データ`) };
          }
        }

        // 年月データの数値チェック
        Object.entries(row.monthlyData).forEach(([yearMonth, value]) => {
          if (!isValidNumber(value)) {
            rowErrors[`monthlyData.${yearMonth}`] = { message: MSG.numeric(yearMonth) };
          } else if (isPercentageField && !isValidPercentage(value)) {
            // パーセンテージ範囲チェック（Yieldシート用）
            rowErrors[`monthlyData.${yearMonth}`] = { message: MSG.percentage(yearMonth) };
          }
        });

        if (Object.keys(rowErrors).length > 0) {
          errors[sheetKey][index] = rowErrors;
        }
      });
    };

    // ■ Cycletimeシートのバリデーション
    validateSheetWithMonthlyData(
      data.cycletime.dataRows,
      "cycletime",
      false
    );

    // ■ Yieldシートのバリデーション（パーセンテージ範囲チェック）
    validateSheetWithMonthlyData(
      data.yield.dataRows,
      "yield",
      true
    );

    return errors;
  };

  /**
   * セルにエラーがあるかチェック
   * @param sheetKey シートキー
   * @param rowIndex 行インデックス
   * @param fieldName フィールド名
   * @returns エラー情報（エラーがない場合はundefined）
   */
  const getCellError = (
    sheetKey: keyof ValidationErrors,
    rowIndex: number,
    fieldName: string
  ): CellError | undefined => {
    return validationErrors[sheetKey]?.[rowIndex]?.[fieldName];
  };

  /**
   * エラー表示付きセルを生成
   * @param sheetKey シートキー
   * @param rowIndex 行インデックス
   * @param fieldName フィールド名
   * @param value 表示値
   * @param tdProps Tdコンポーネントに渡すプロパティ
   * @returns Td要素
   */
  const renderErrorCell = (
    sheetKey: keyof ValidationErrors,
    rowIndex: number,
    fieldName: string,
    value: React.ReactNode,
    tdProps?: React.ComponentProps<typeof Td>
  ) => {
    const error = getCellError(sheetKey, rowIndex, fieldName);
    const hasError = !!error;

    // エラー時のスタイル（インラインスタイルで強制適用）
    const errorStyle = hasError ? {
      border: "2px solid #E53E3E",  // red.500
      backgroundColor: "#FED7D7",   // red.100（より目立つ色に変更）
    } : {};

    const cellContent = (
      <Td
        {...tdProps}
        isTruncated={true}
        style={{
          ...errorStyle,
          ...(tdProps as any)?.style,
        }}
      >
        {value ?? ""}
      </Td>
    );

    if (hasError) {
      return (
        <Tooltip
          label={error.message}
          hasArrow
          bg="red.500"
          color="white"
          placement="top"
          borderRadius={5}
          padding={2}
        >
          {cellContent}
        </Tooltip>
      );
    }

    return cellContent;
  };

  /**
   * バリデーションエラーがあるかどうかを判定
   * @returns エラーがある場合true
   */
  const hasValidationErrors = (): boolean => {
    return Object.values(validationErrors).some(sheetErrors =>
      Object.keys(sheetErrors).length > 0
    );
  };

  /**
   * spreadSheet関数情報生成
   * @param sheetId シートID
   * @param columnNameList カラム名（位置検索用）
   * @param targetColumns 対象カラム
   * @param startRow 開始行（1オリジン）
   * @param formulaRow 関数設定行（1オリジン）
   * @param formulaName SpreadSheet関数名
   * @returns
   */
  const generateCellFormuras = (sheetId: number, columnNameList: string[], targetColumns: string[], startRow: number, formulaRow: number, formulaName: string) => {
    const formulaList: CellFormula[] = [];

    for (const targetColumn of targetColumns) {
      // 数式対象カラム位置
      const col = columnNameList.findIndex(columnName => columnName === targetColumn) + 1;
      // 1 ⇒ Aに変換
      const colA = googleAPI.columnToLetter(col);

      formulaList.push({
        sheetId: sheetId,
        row: formulaRow,
        col: col,
        formula: `=${formulaName}(${colA}${startRow}:${colA})`
      });
    }

    return formulaList;
  }

  /**
   * 材料費用 SumIf 関数生成
   * @param sheetId シートID
   * @param startRow 開始行（1オリジン）
   * @param headerRow ヘッダ行
   * @param typeName 対象タイプ
   * @param dataStartRow データ行開始位置
   * @returns 
   */
  const generateCellForSumIf = (sheetId: number, startRow: number, headerRow: string[], typeName: string, dataStartRow: number) => {
    const formulaList: CellFormula[] = [];

    // Type列位置
    const typeColumnPos = headerRow.findIndex(header => header === columnNames.materialCost.Type) + 1;
    const startCol = typeColumnPos + 1;

    // カラム位置 ⇒ Aに変換
    const typeColumnCol = googleAPI.columnToLetter(typeColumnPos);

    yearMonthList.forEach((_, idx) => {
      // カラム位置 ⇒ Aに変換
      const valueCol = googleAPI.columnToLetter(startCol + idx);
      formulaList.push({
        sheetId: sheetId,
        row: startRow,
        col: startCol + idx,
        formula: `=sumif(${typeColumnCol}$${dataStartRow}:${typeColumnCol},"${typeName}",${valueCol}$${dataStartRow}:${valueCol})`
      });
    })

    return formulaList;
  }

  /**
   * 材料費用 Product 関数生成
   * @param sheetId シートID
   * @param startRow 開始行（1オリジン）
   * @param headerRow ヘッダ行
   * @param typeName 対象タイプ
   * @param dataStartRow データ行開始位置
   * @returns 
   */
  const generateCellForProduct = (sheetId: number, startRow: number, headerRow: string[], typeName: string, dataStartRow: number) => {
    const formulaList: CellFormula[] = [];

    // Type列位置
    const typeColumnPos = headerRow.findIndex(header => header === columnNames.materialCost.Type) + 1;
    const startCol = typeColumnPos + 1;

    // カラム位置 ⇒ Aに変換
    const typeColumnCol = googleAPI.columnToLetter(typeColumnPos);

    yearMonthList.forEach((_, idx) => {
      // カラム位置 ⇒ Aに変換
      const valueCol = googleAPI.columnToLetter(startCol + idx);
      formulaList.push({
        sheetId: sheetId,
        row: startRow,
        col: startCol + idx,
        formula: `=iferror(product(filter(${valueCol}$${dataStartRow}:${valueCol}, ${typeColumnCol}$${dataStartRow}:${typeColumnCol}="${typeName}")),"")`,
      });
    })

    return formulaList;
  }

  /**
   * 書式設定情報生成
   * @param sheetId シートID
   * @param columnNameList カラム名（位置検索用）
   * @param targetColumns 対象カラム
   * @param startRow 開始行（1オリジン）
   * @param dataRowLength データ行の長さ
   * @param format 書式
   * @param colLength 適用列数
   * @returns
   */
  const generateCellFormat = (sheetId: number, columnNameList: string[], targetColumns: string[], startRow: number, dataRowLength: number | undefined, format: any, colLength?: number): CellFormatRange[] => {
    if (!dataRowLength) {
      dataRowLength = 0;
    }

    const formatObjList: CellFormatRange[] = [];

    for (const targetColumn of targetColumns) {
      const startCol = columnNameList.findIndex(columnName => columnName === targetColumn) + 1;

      formatObjList.push({
        sheetId: sheetId,
        startRow: startRow,
        endRow: startRow + dataRowLength - 1,
        startCol: startCol,
        endCol: startCol + (colLength || 0),
        format: format
      });
    }

    return formatObjList;
  }

  /**
   * 書式設定情報生成（MaterialCostシート専用）
   * @param sheetId シートID
   * @param columnNameList カラム名（位置検索用）
   * @param targetColumns 対象カラム
   * @param startRow 開始行（1オリジン）
   * @param dataRowLength データ行の長さ
   * @param format 書式
   * @param colLength 適用列数
   * @returns 
   */
  const generateCellFormatForMaterialCost = (sheetId: number, columnNameList: string[], targetColumns: string[], startRow: number, dataRowLength: number | undefined, format: any, colLength?: number): CellFormatRange[] => {
    const formatObjList: CellFormatRange[] = [];

    let materialCostRowCount = 0;

    bomSheetsData.processBom.dataRows.forEach((row, idx) => {
      if (row.isMaterialCostRow) {
        materialCostRowCount++;
        const targetRow = startRow + idx + (3 * materialCostRowCount) - 1;
        formatObjList.push(...generateCellFormat(sheetId, columnNameList, targetColumns, targetRow, 1, format, colLength));
        formatObjList.push(...generateCellFormat(sheetId, columnNameList, targetColumns, targetRow + 1, 1, format, colLength));
      }
    });

    return formatObjList;
  }

  /**
   * プルダウン項目情報生成
   * @param sheetId シートID
   * @param columnNameList カラム名（位置検索用）
   * @param targetColumns 対象カラム
   * @param startRow 開始行（1オリジン）
   * @param dataRowLength データ行の長さ
   * @param itemList プルダウン項目
   * @returns 
   */
  const generateDataValidation = (sheetId: number, columnNameList: string[], targetColumns: string[], startRow: number, dataRowLength: number | undefined, itemList: string[]) => {
    if (!dataRowLength || dataRowLength === 0) {
      return [];
    }

    const dataValidationObjList: DataValidationRange[] = [];

    for (const targetColumn of targetColumns) {
      const startCol = columnNameList.findIndex(columnName => columnName === targetColumn) + 1;

      dataValidationObjList.push({
        sheetId: sheetId,
        startRow: startRow,
        endRow: startRow + dataRowLength - 1,
        startCol: startCol,
        endCol: startCol,
        values: itemList,
      });
    }

    return dataValidationObjList;
  }

  /**
   * 対象オブジェクトのキー変更処理
   * @param record 対象オブジェクト
   * @param convertFunc キー変更処理
   * @returns 
   */
  const convertRecordKey = (record: Record<string, any> | null | undefined, convertFunc: (val: string) => string) => {
    if (!record) {
      return record;
    }

    const result = Object.entries(record).reduce((acc, [key, value]) => {
      const newKey = convertFunc(key)
      acc[newKey] = value;
      return acc;
    }, {} as Record<string, any>);

    return result;
  }

  /**
   * 指定したシートにデータを設定する。
   * @param activeSpreadsheetId シートID
   * @param sheetName シート名
   * @param headerNameList ヘッダ名リスト（共通部を除く個別部分のみ）
   * @param dataRows データ行
   * @param arrayColumnName 配列カラム名
   * @param convertFuncs カラム毎変換処理
   * @param excludeColumnNames 除外カラムリスト
   * @returns 
   */
  const inputSpreadSheetData = async <T,>(
    activeSpreadsheetId: string,
    sheetName: string,
    headerNameList: string[][],
    dataRows: T[] | undefined,
    arrayColumnName: string | null | undefined,
    convertFuncs: (Record<string, (val: string) => string>) | null | undefined,
    excludeColumnNames?: string[],
    api?: GoogleAPI, // 書き込みに使うAPI（GoogleAPIBatchCollector を渡すとまとめて送信。未指定時は即時送信）
  ) => {

    const functionName = "inputSpreadSheetData";

    try {

      if (!dataRows) {
        return false;
      }

      const capitalizeFirst = (str: string): string =>
        str.charAt(0).toUpperCase() + str.slice(1);

      // キーにスペースが含まれていれば、削除
      convertFuncs = convertRecordKey(convertFuncs, (value: string) => value.replace(" ", ""));

      // データ行
      const values = dataRows.map((row: any) => {
        const array: string[] = [];
        // カラム毎ループ
        for (let [key, value] of Object.entries(row)) {
          if (excludeColumnNames?.includes(key)) {
            continue;
          }

          if (arrayColumnName === key) {
            // 配列カラムの場合
            array.push(...Object.values(value as any).map(sub => {
              if (convertFuncs && (key in convertFuncs)) {
                // 変換処理
                sub = convertFuncs[key](String(sub));
              }
              return String(sub);
            }));
          } else {
            const pascalKey = capitalizeFirst(key);
            if (convertFuncs && (pascalKey in convertFuncs)) {
              // 変換処理
              value = convertFuncs[pascalKey](String(value));
            }

            // 単一カラムの場合
            array.push(String(value));
          }
        }

        return array;
      });

      // 全データ
      const data: string[][] = [
        ...headerNameList,
        ...values,
      ];

      // セル範囲
      const range = googleAPI.generateRange(sheetName, data);

      // データクリアは不要（呼び出し元の openSpreadsheetInternal で常に新規作成したシートに書き込むため）

      // セル値設定
      await (api ?? googleAPI).spreadSheetSetValueRange(activeSpreadsheetId, range, data);

      return true;

    } catch (error) {
      console.error(`${functionName} Error`, sheetName, headerNameList, dataRows, arrayColumnName, error);
      throw new Error(`${functionName} Error`);
    }
  }

  /**
   * MaterialCostシートにデータを設定する。
   * @param activeSpreadsheetId シートID
   * @param sheetName シート名
   * @param headerNameList ヘッダ名リスト（共通部を除く個別部分のみ）
   * @param dataRows データ行
   * @param div100 パーセンテージ値変換処理
   * @param excludeColumnNames 除外カラムリスト
   * @returns 
   */
  const inputSpreadSheetDataForMaterialCost = async (
    activeSpreadsheetId: string,
    sheetName: string,
    headerNameList: string[][],
    dataRows: MaterialCostRow[] | undefined,
    div100: (val: string) => string,
    excludeColumnNames?: string[],
    currencyList?: Currency[],
    api?: GoogleAPI, // 書き込みに使うAPI（GoogleAPIBatchCollector を渡すとまとめて送信。未指定時は即時送信）
  ) => {

    const functionName = "inputSpreadSheetDataForMaterialCost";

    try {

      if (!dataRows) {
        return false;
      }

      // ヘッダ部最大長取得
      const maxHeaderLength = headerNameList.reduce((acc, headerNameRow) => {
        if (acc < headerNameRow.length) {
          acc = headerNameRow.length;
        }
        return acc;
      }, 0);

      // 配列リサイズ
      const resizeArray = (baseArray: string[], newLength: number) => Array.from({ length: newLength }, (_, i) => baseArray[i] ?? "");

      // 空配列作成
      const emptyArray = (length: number) => Array.from({ length: length }, () => "");

      // データ行
      const values: string[][] = [];
      for (let idx = 0; idx < dataRows.length; idx++) {

        const row = dataRows[idx];

        const array: string[] = [];

        // 材料費行判定フラグ
        const isMaterialCostRow = bomSheetsData.processBom.dataRows[idx]?.isMaterialCostRow;

        // 全データ配列化
        const entries = Object.entries(row);

        // 固定共通列
        const fixedColumnValues = entries.filter(([key, value]) => !key.endsWith("Monthly") && !excludeColumnNames?.includes(key)).map(([key, value]) => String(value));

        // 購入通貨名を取得（purchaseCurrencyIdから通貨名に変換）
        const purchaseCurrencyName = currencyList?.find(c => c.currencyId === row.purchaseCurrencyId)?.currencyName || "";

        // Usage値
        const usageValue = row.usage || "";

        if (!isMaterialCostRow) {
          // プロセス行
          array.push(...fixedColumnValues);
          // 購入通貨列（空欄）
          array.push("");
          // Usage列
          array.push(usageValue);

          // 行追加
          values.push(resizeArray(array, maxHeaderLength));
        } else {
          // 材料費行
          array.push(...fixedColumnValues);
          // 購入通貨列
          array.push(purchaseCurrencyName);
          // Usage列
          array.push(usageValue);

          // 空配列作成（固定共通列 + 購入通貨列 + Usage列分）
          const upExternalRow: string[] = emptyArray(fixedColumnValues.length + 2);
          const upInhouseRatioRow: string[] = emptyArray(fixedColumnValues.length + 2);
          const upMaterialYieldRow: string[] = emptyArray(fixedColumnValues.length + 2);

          // Type列 設定
          array.push(columnNames.materialCost.UnitPriceInhouse);
          upExternalRow.push(columnNames.materialCost.UnitPriceExternal);
          upInhouseRatioRow.push(columnNames.materialCost.InhouseRatio);
          upMaterialYieldRow.push(columnNames.materialCost.MaterialYield);

          // 年月別データ設定
          array.push(...Object.values(row.unitPriceInhouseMonthly));
          upExternalRow.push(...Object.values(row.unitPriceExternalMonthly));
          upInhouseRatioRow.push(...Object.values(row.inhouseRatioMonthly).map(v => div100(v)));
          upMaterialYieldRow.push(...Object.values(row.materialYieldMonthly).map(v => div100(v)));

          // 行追加
          values.push(resizeArray(array, maxHeaderLength));
          values.push(resizeArray(upExternalRow, maxHeaderLength));
          values.push(resizeArray(upInhouseRatioRow, maxHeaderLength));
          values.push(resizeArray(upMaterialYieldRow, maxHeaderLength));
        }
      };

      // 全データ
      const data: string[][] = [
        ...headerNameList,
        ...values,
      ];

      console.log(`全データ`, data);

      // セル範囲
      const range = googleAPI.generateRange(sheetName, data);

      // データクリアは不要（呼び出し元の openSpreadsheetInternal で常に新規作成したシートに書き込むため）


      // セル値設定
      await (api ?? googleAPI).spreadSheetSetValueRange(activeSpreadsheetId, range, data);

      return true;

    } catch (error) {
      console.error(`${functionName} Error`, sheetName, headerNameList, dataRows, error);
      throw new Error(`${functionName} Error`);
    }
  }

  /**
   * 開始日～終了日までの年月リストを生成
   * @param startDate 開始日（yyyy/mm/dd または yyyy-mm-dd）
   * @param endDate 終了日（yyyy/mm/dd または yyyy-mm-dd）
   * @returns 年月リスト（yyyy-mm形式の配列）
   */
  const generateYearMonthList = (startDate: string, endDate: string): string[] => {
    const result: string[] = [];

    // 日付をパース（yyyy/mm/dd または yyyy-mm-dd に対応）
    const start = dayjs(startDate);
    const end = dayjs(endDate);

    // 無効な日付の場合は空配列を返す
    if (!start.isValid() || !end.isValid()) {
      return result;
    }

    // 開始日が終了日より後の場合は空配列を返す
    if (start.isAfter(end)) {
      return result;
    }

    // 開始月から終了月まで繰り返し
    let current = start.startOf("month");
    const endMonth = end.startOf("month");

    while (current.isBefore(endMonth) || current.isSame(endMonth)) {
      result.push(current.format("YYYY-MM"));
      current = current.add(1, "month");
    }

    return result;
  };

  /**
   * 積を計算
   * @param dataRows すべてのデータ行
   * @param propertyName 集計対象プロパティ名
   * @returns 積
   */
  const multiply = <T, PT extends keyof T>(dataRows: T[] | undefined, propertyName: PT) => {
    const funcName = "multiply";
    try {
      if (!dataRows) {
        return null;
      }

      let hasValue = false;

      const result = dataRows.reduce((acc: Decimal, row: T) => {
        let tempValue = String(row[propertyName]) || null;

        if (!tempValue) {
          // 空白は計算対象外
          return acc;
        }

        hasValue = true;

        let value = new Decimal(isNaN(Number(tempValue)) ? 0 : tempValue);
        acc = acc.mul(value.div(100));

        return acc;
      }, new Decimal(1));

      if (!hasValue) {
        return null;
      }

      // 表示用に変換
      return result.mul(100);
    } catch (error) {
      console.error(`${funcName} propertyName = ${String(propertyName)} error =`, error);
      return new Decimal(0);
    }
  }

  /**
   * 合計を計算
   * @param dataRows すべてのデータ行
   * @param propertyName 集計対象プロパティ名
   * @returns 合計
   */
  const total = <T, PT extends keyof T>(dataRows: T[] | undefined, propertyName: PT) => {
    const funcName = "sumValue";
    try {
      if (!dataRows) {
        return null;
      }

      let hasValue = false;

      const result = dataRows.reduce((acc: Decimal, row: T) => {
        let tempValue = String(row[propertyName]) || null;

        if (!tempValue) {
          tempValue = "0";
        } else {
          hasValue = true;
        }

        let value = new Decimal(isNaN(Number(tempValue)) ? 0 : tempValue);
        acc = acc.plus(value);
        return acc;
      }, new Decimal(0));

      if (!hasValue) {
        return null;
      }

      return result;
    } catch (error) {
      console.error(`${funcName} propertyName = ${String(propertyName)} error =`, error);
      return new Decimal(0);
    }
  }

  /**
   * 単位でフォーマット
   * @param value 値
   * @param unit 単位
   * @param decimalPlaces 小数点以下桁数
   * @returns 
   */
  const displayUnit = (value: string | null | undefined, unit: string | null, decimalPlaces?: number) => {
    try {
      if (value && value !== "" && !isNaN(Number(value))) {
        const decimalValue = new Decimal(value);
        if (decimalPlaces) {
          value = decimalValue.toFixed(decimalPlaces);
        } else {
          value = decimalPlaces?.toString();
        }
      }
    } catch (error) {
      console.log(`displayUnit error value = ${value}, `, error);
    }

    if (!unit) {
      return (value || null) ? value : ``;
    }

    return (value || null) ? `${value} ${unit}` : ``;
  }

  /**
   * 累積列幅を返す（表示列の状態を考慮）
   * @param columnPos 列番号(1オリジン)
   * @returns 累積列幅
   */
  const getFixedColumnLeft = (columnPos: number, isMaterialCostTab: boolean = false) => {
    let width: number = 0;

    // Seq（columnPos=1）は常に表示
    if (1 < columnPos) width += WIDTH_SEQ;
    // Level（columnPos=2）
    if (2 < columnPos && visibleColumns.level) width += WIDTH_LEVEL;
    // Model No.（columnPos=3）
    if (3 < columnPos && visibleColumns.modelNo) width += WIDTH_MODEL_NO;
    // Process Code（columnPos=4）
    if (4 < columnPos && visibleColumns.processCode) width += WIDTH_PROCESS_CODE;
    // Item No.（columnPos=5）
    if (5 < columnPos && visibleColumns.itemNo) width += WIDTH_ITEM_NO;
    // Item Name（columnPos=6）
    if (6 < columnPos && visibleColumns.itemName) width += WIDTH_ITEM_NAME;

    // 購入通貨（columnPos=7）MaterialCostタブ専用
    if (isMaterialCostTab && 7 < columnPos) width += WIDTH_PURCHASE_CURRENCY;
    // Usage（columnPos=8）MaterialCostタブ専用
    if (isMaterialCostTab && 8 < columnPos) width += WIDTH_USAGE;
    // Type（columnPos=9）MaterialCostタブ専用
    if (isMaterialCostTab && 9 < columnPos) width += WIDTH_TYPE;

    return width;
  }
  /**
   * Total列の累積列幅を返す（Cycletime Or Yieldタブ専用）
   * @returns 累積列幅
   */
  const getFixedTotalColumnLeft = () => {
    let width: number = 0;

    // Seq
    if (visibleColumns.level || visibleColumns.modelNo || visibleColumns.processCode || visibleColumns.itemNo || visibleColumns.itemName) {
      width += WIDTH_SEQ;
    }
    // Level
    if (visibleColumns.level && (visibleColumns.modelNo || visibleColumns.processCode || visibleColumns.itemNo || visibleColumns.itemName)) {
      width += WIDTH_LEVEL;
    }
    // Model No.
    if (visibleColumns.modelNo && (visibleColumns.processCode || visibleColumns.itemNo || visibleColumns.itemName)) {
      width += WIDTH_MODEL_NO;
    }
    // Process Code
    if (visibleColumns.processCode && (visibleColumns.itemNo || visibleColumns.itemName)) {
      width += WIDTH_PROCESS_CODE;
    }
    // Item No.
    if (visibleColumns.itemNo && (visibleColumns.itemName)) {
      width += WIDTH_ITEM_NO;
    }

    return width;
  }

  /**
   * 表示列の数を取得（Seq + 可視列）
   * @returns 表示されている固定列の数
   */
  const getVisibleFixedColumnCount = () => {
    let count = 1; // Seqは常に表示
    if (visibleColumns.level) count++;
    if (visibleColumns.modelNo) count++;
    if (visibleColumns.processCode) count++;
    if (visibleColumns.itemNo) count++;
    if (visibleColumns.itemName) count++;
    return count;
  }

  /**
   * ProcessBOM(initial) テーブルコンポーネント
   * @param sheetData シートデータ
   * @returns 
   */
  const generateTableForProcessBOM = (sheetData: ProcessBomInitialSheetData | undefined) => {

    return (
      <Table variant="simple" size="sm" style={{ tableLayout: "fixed", borderCollapse: "separate" }} cellSpacing={0} className=" mm-ceas-table-extend">
        <colgroup>
          <col width={`${WIDTH_SEQ}px`} />
          {visibleColumns.level && <col width={`${WIDTH_LEVEL}px`} />}
          {visibleColumns.modelNo && <col width={`${WIDTH_MODEL_NO}px`} />}
          {visibleColumns.processCode && <col width={`${WIDTH_PROCESS_CODE}px`} />}
          {visibleColumns.itemNo && <col width={`${WIDTH_ITEM_NO}px`} />}
          {visibleColumns.itemName && <col width={`${WIDTH_ITEM_NAME}px`} />}
          <col width="110px" /> {/* IsCustomerSpecifiedParts */}
          <col width={`${115}px`} /> {/* tooltipの追加に伴い、90 -> 115に変更 */}
          <col width={`*`} /> {/* 幅揃えのためのダミー列 */}
        </colgroup>
        <Thead position="sticky" top={0} bg="white" zIndex="10">
          <Tr>
            <Th position="sticky" left={`${getFixedColumnLeft(1)}px`} zIndex="10" textTransform="none">{columnNames.common.Seq}</Th>
            {visibleColumns.level && <Th position="sticky" left={`${getFixedColumnLeft(2)}px`} zIndex="10" textTransform="none">{columnNames.common.Level}</Th>}
            {visibleColumns.modelNo && <Th position="sticky" left={`${getFixedColumnLeft(3)}px`} zIndex="10" textTransform="none">{columnNames.common.ModelNo}</Th>}
            {visibleColumns.processCode &&
              <Th position="sticky" left={`${getFixedColumnLeft(4)}px`} zIndex="10" textTransform="none">
                <Flex align="center" gap={2}>
                  {columnNames.common.ProcessCode}
                  <HelpIcon size="20">{t('features.InvestmentScreening.BomInfoList.tooltip.processCode')}</HelpIcon>
                </Flex>
              </Th>}
            {visibleColumns.itemNo &&
              <Th position="sticky" left={`${getFixedColumnLeft(5)}px`} zIndex="10" textTransform="none">
                <Flex align="center" gap={2}>
                  {columnNames.common.ItemNo}
                  <HelpIcon size="20">{t('features.InvestmentScreening.BomInfoList.tooltip.itemNo')}</HelpIcon>
                </Flex>
              </Th>}
            {visibleColumns.itemName && <Th position="sticky" left={`${getFixedColumnLeft(6)}px`} zIndex="10" textTransform="none">{columnNames.common.ItemName}</Th>}
            <Th textTransform="none">{columnNames.processBom.IsCustomerSpecifiedParts}</Th>
            <Th textTransform="none">
              <Flex align="center" gap={2}>
                {t('features.InvestmentScreening.BomInfoList.column.material')}
                <HelpIcon size="20">
                  {t('features.InvestmentScreening.BomInfoList.tooltip.material')}
                </HelpIcon>
              </Flex>
            </Th>
            <th></th> {/* 幅揃えのためのダミー列 */}
          </Tr>
        </Thead>
        <Tbody>
          {sheetData?.dataRows.map((row, idx) => {
            return (
              <Tr key={idx} h={`${HEIGHT_ROW}px`}>
                {renderErrorCell("processBom", idx, "seq", row.seq, { position: "sticky", left: `${getFixedColumnLeft(1)}px`, zIndex: "1" })}
                {visibleColumns.level && renderErrorCell("processBom", idx, "level", row.level, { position: "sticky", left: `${getFixedColumnLeft(2)}px`, zIndex: "1" })}
                {visibleColumns.modelNo && <Td position="sticky" left={`${getFixedColumnLeft(3)}px`} zIndex="1" isTruncated={true}>{row.modelNo}</Td>}
                {visibleColumns.processCode && renderErrorCell("processBom", idx, "processCode", row.processCode, { position: "sticky", left: `${getFixedColumnLeft(4)}px`, zIndex: "1" })}
                {visibleColumns.itemNo && renderErrorCell("processBom", idx, "itemNo", row.itemNo, { position: "sticky", left: `${getFixedColumnLeft(5)}px`, zIndex: "1" })}
                {visibleColumns.itemName && renderErrorCell("processBom", idx, "itemName", row.itemName, { position: "sticky", left: `${getFixedColumnLeft(6)}px`, zIndex: "1" })}
                {renderErrorCell("processBom", idx, "isCustomerSpecifiedParts", row.isCustomerSpecifiedParts ? <CheckIcon color="green" /> : null, { textAlign: "center" })}
                <Td textAlign="center">
                  <Switch sx={{ mr: 0 }} isChecked={row.isMaterialCostRow} isDisabled={!(props.pagePermissions?.isEditable || false)} onChange={(event) => {
                    changeProcessBom(idx, "isMaterialCostRow", event.target.checked);
                  }} />
                </Td>
                <td></td> {/* 幅揃えのためのダミー列 */}
              </Tr>
            )
          })}
        </Tbody>
      </Table>
    );
  }

  /**
   * MaterialCost テーブルコンポーネント
   * @param sheetData シートデータ
   * @returns
   */
  const generateTableForMaterialCost = (sheetData: MaterialCostSheetData | undefined) => {

    return (
      <Table variant="simple" size="sm" style={{ tableLayout: "fixed", borderCollapse: "separate" }} cellSpacing={0} className="mm-ceas-table-extend">
        <colgroup>
          <col width={`${WIDTH_SEQ}px`} />
          {visibleColumns.level && <col width={`${WIDTH_LEVEL}px`} />}
          {visibleColumns.modelNo && <col width={`${WIDTH_MODEL_NO}px`} />}
          {visibleColumns.processCode && <col width={`${WIDTH_PROCESS_CODE}px`} />}
          {visibleColumns.itemNo && <col width={`${WIDTH_ITEM_NO}px`} />}
          {visibleColumns.itemName && <col width={`${WIDTH_ITEM_NAME}px`} />}

          <col width={`${WIDTH_PURCHASE_CURRENCY}px`} /> {/* 購入通貨 */}
          <col width={`${WIDTH_USAGE}px`} /> {/* Usage */}
          <col width={`${WIDTH_TYPE}px`} /> {/* Type */}

          {yearMonthList.map((yearMonth) => <col key={yearMonth} width={`${WIDTH_UNIT_PRICE}px`} />)}
          <col width="*" />
        </colgroup>
        <Thead position="sticky" top={0} bg="white" zIndex="10" >
          <Tr>
            <Th position="sticky" left={`${getFixedColumnLeft(1)}px`} zIndex="10" textTransform="none">{columnNames.common.Seq}</Th>
            {visibleColumns.level && <Th position="sticky" left={`${getFixedColumnLeft(2)}px`} zIndex="10" textTransform="none">{columnNames.common.Level}</Th>}
            {visibleColumns.modelNo && <Th position="sticky" left={`${getFixedColumnLeft(3)}px`} zIndex="10" textTransform="none">{columnNames.common.ModelNo}</Th>}
            {visibleColumns.processCode && <Th position="sticky" left={`${getFixedColumnLeft(4)}px`} zIndex="10" textTransform="none">{columnNames.common.ProcessCode}</Th>}
            {visibleColumns.itemNo && <Th position="sticky" left={`${getFixedColumnLeft(5)}px`} zIndex="10" textTransform="none">{columnNames.common.ItemNo}</Th>}
            {visibleColumns.itemName && <Th position="sticky" left={`${getFixedColumnLeft(6)}px`} zIndex="10" textTransform="none">{columnNames.common.ItemName}</Th>}

            <Th position="sticky" left={`${getFixedColumnLeft(7, true)}px`} zIndex="10" textTransform="none">{columnNames.materialCost.PurchaseCurrency}</Th>
            <Th position="sticky" left={`${getFixedColumnLeft(8, true)}px`} zIndex="10" textTransform="none">{columnNames.materialCost.Usage}</Th>
            <Th position="sticky" left={`${getFixedColumnLeft(9, true)}px`} zIndex="10" textTransform="none">{columnNames.materialCost.Type}</Th>

            {yearMonthList.map((yearMonth) => <Th key={yearMonth} textTransform="none">{yearMonth}</Th>)}
            <Th>{/* レイアウト補正用 */}</Th>
          </Tr>
        </Thead>
        <Tbody>
          {sheetData?.dataRows.map((row, idx) => {
            const isMaterialCostRow = (bomSheetsData?.processBom?.dataRows[idx]?.isMaterialCostRow || false);
            const rowSpan = isMaterialCostRow ? 6 : 1;

            return (
              <>
                <Tr key={idx} h={`${HEIGHT_ROW}px`}>
                  {renderErrorCell("materialCost", idx, "seq", row.seq, { position: "sticky", left: `${getFixedColumnLeft(1)}px`, zIndex: "1", rowSpan: rowSpan })}
                  {visibleColumns.level && renderErrorCell("materialCost", idx, "level", row.level, { position: "sticky", left: `${getFixedColumnLeft(2)}px`, zIndex: "1", rowSpan: rowSpan })}
                  {visibleColumns.modelNo && renderErrorCell("materialCost", idx, "modelNo", row.modelNo, { position: "sticky", left: `${getFixedColumnLeft(3)}px`, zIndex: "1", rowSpan: rowSpan })}
                  {visibleColumns.processCode && renderErrorCell("materialCost", idx, "processCode", row.processCode, { position: "sticky", left: `${getFixedColumnLeft(4)}px`, zIndex: "1", rowSpan: rowSpan })}
                  {visibleColumns.itemNo && renderErrorCell("materialCost", idx, "itemNo", row.itemNo, { position: "sticky", left: `${getFixedColumnLeft(5)}px`, zIndex: "1", rowSpan: rowSpan })}
                  {visibleColumns.itemName && renderErrorCell("materialCost", idx, "itemName", row.itemName, { position: "sticky", left: `${getFixedColumnLeft(6)}px`, zIndex: "1", rowSpan: rowSpan })}
                  {renderErrorCell("materialCost", idx, "purchaseCurrencyId", props.currencyList.find(c => c.currencyId === row.purchaseCurrencyId)?.currencyName, { position: "sticky", left: `${getFixedColumnLeft(7, true)}px`, zIndex: "1", rowSpan: rowSpan })}
                  {renderErrorCell("materialCost", idx, "usage", displayUnit(row.usage, null, 4), { position: "sticky", left: `${getFixedColumnLeft(8, true)}px`, zIndex: "1", textAlign: "right", rowSpan: rowSpan })}

                  {!isMaterialCostRow ? (
                    <>
                      {/* プロセス行の場合 */}
                      <Td position="sticky" left={`${getFixedColumnLeft(9, true)}px`} zIndex="1">{/* \Type */}</Td>
                      {yearMonthList.map((_) => <td></td>)}
                    </>
                  ) : (
                    <>
                      {/* 材料費行の場合 */}
                      <Th position="sticky" left={`${getFixedColumnLeft(9, true)}px`} zIndex="1" textTransform="none">{columnNames.materialCost.UnitPriceCombined}</Th>
                      {yearMonthList.map((yearMonth) => renderErrorCell("materialCost", idx, `unitPriceCombinedMonthly.${yearMonth}`, displayUnit(row.unitPriceCombinedMonthly?.[yearMonth], null, 4), { textAlign: "right", key: yearMonth }))}
                    </>
                  )}
                  <Td>{/* レイアウト補正用 */}</Td>
                </Tr>
                {isMaterialCostRow && (
                  <>
                    {/* 材料費行の場合 */}
                    <Tr key={idx + columnNames.materialCost.UnitPriceInhouse} h={`${HEIGHT_ROW}px`}>
                      <Th position="sticky" left={`${getFixedColumnLeft(9, true)}px`} zIndex="1" textTransform="none">{columnNames.materialCost.UnitPriceInhouse}</Th>
                      {yearMonthList.map((yearMonth) => renderErrorCell("materialCost", idx, `unitPriceInhouseMonthly.${yearMonth}`, displayUnit(row.unitPriceInhouseMonthly?.[yearMonth], null, 4), { textAlign: "right", key: yearMonth }))}
                      <td>{/* レイアウト補正用 */}</td>
                    </Tr>
                    <Tr key={idx + columnNames.materialCost.UnitPriceExternal} h={`${HEIGHT_ROW}px`}>
                      <Th position="sticky" left={`${getFixedColumnLeft(9, true)}px`} zIndex="1" textTransform="none">{columnNames.materialCost.UnitPriceExternal}</Th>
                      {yearMonthList.map((yearMonth) => renderErrorCell("materialCost", idx, `unitPriceExternalMonthly.${yearMonth}`, displayUnit(row.unitPriceExternalMonthly?.[yearMonth], null, 4), { textAlign: "right", key: yearMonth }))}
                      <td>{/* レイアウト補正用 */}</td>
                    </Tr>
                    <Tr key={idx + columnNames.materialCost.InhouseRatio} h={`${HEIGHT_ROW}px`} >
                      <Th position="sticky" left={`${getFixedColumnLeft(9, true)}px`} zIndex="1" textTransform="none">{columnNames.materialCost.InhouseRatio}</Th>
                      {yearMonthList.map((yearMonth) => renderErrorCell("materialCost", idx, `inhouseRatioMonthly.${yearMonth}`, displayUnit(row.inhouseRatioMonthly?.[yearMonth], "%", 2), { textAlign: "right", key: yearMonth }))}
                      <td>{/* レイアウト補正用 */}</td>
                    </Tr>
                    <Tr key={idx + columnNames.materialCost.MaterialYield} h={`${HEIGHT_ROW}px`}>
                      <Th position="sticky" left={`${getFixedColumnLeft(9, true)}px`} zIndex="1" textTransform="none">{columnNames.materialCost.MaterialYield}</Th>
                      {yearMonthList.map((yearMonth) => renderErrorCell("materialCost", idx, `materialYieldMonthly.${yearMonth}`, displayUnit(row.materialYieldMonthly?.[yearMonth], "%", 2), { textAlign: "right", key: yearMonth }))}
                      <td>{/* レイアウト補正用 */}</td>
                    </Tr>
                    <Tr key={idx + columnNames.materialCost.Amount} h={`${HEIGHT_ROW}px`}>
                      <Th position="sticky" left={`${getFixedColumnLeft(9, true)}px`} zIndex="1" textTransform="none">{columnNames.materialCost.Amount}</Th>
                      {yearMonthList.map((yearMonth) => renderErrorCell("materialCost", idx, `amountMonthly.${yearMonth}`, displayUnit(row.amountMonthly?.[yearMonth], null, 4), { textAlign: "right", key: yearMonth }))}
                      <td>{/* レイアウト補正用 */}</td>
                    </Tr>
                  </>
                )}
              </>
            )
          })}
        </Tbody >
        <Tfoot position="sticky" bottom={0} bg="white" zIndex="10" maxH={0}>
          {isMaterialCostTotalExpanded ? (
            <>
              {/* 展開時：6行表示 */}
              <Tr h={`${HEIGHT_ROW}px`}>
                <Td colSpan={getVisibleFixedColumnCount()} rowSpan={6} position="sticky" left={`${getFixedColumnLeft(1)}px`} zIndex="1" style={{ borderRightStyle: "none" }}></Td>
                <Td colSpan={2} rowSpan={6} position="sticky" left={`${getFixedColumnLeft(7, true)}px`} zIndex="1" fontWeight="bold" textAlign="right" verticalAlign="middle">
                  <HStack spacing={1} justifyContent="flex-end">
                    <Text>Total</Text>
                    <IconButton
                      aria-label={t('features.InvestmentScreening.BomInfoList.button.collapse')}
                      icon={<ChevronDownIcon />}
                      size="lg"
                      maxH={25}
                      onClick={() => setIsMaterialCostTotalExpanded(false)}
                    />
                  </HStack>
                </Td>
                <Td position="sticky" left={`${getFixedColumnLeft(9, true)}px`} zIndex="10" fontWeight="bold" fontSize={12}>{columnNames.materialCost.UnitPriceCombined}</Td>
                {yearMonthList.map((yearMonth) => <Td key={yearMonth} isTruncated={true} textAlign="right">{total(sheetData?.dataRows.map(row => ({ value: row.unitPriceCombinedMonthly[yearMonth] })), "value")?.toFixed(5)}</Td>)}
                <td>{/* レイアウト補正用 */}</td>
              </Tr>
              <Tr h={`${HEIGHT_ROW}px`}>
                <Td position="sticky" left={`${getFixedColumnLeft(9, true)}px`} zIndex="10" fontWeight="bold" fontSize={12}>{columnNames.materialCost.UnitPriceInhouse}</Td>
                {yearMonthList.map((yearMonth) => <Td key={yearMonth} isTruncated={true} textAlign="right">{total(sheetData?.dataRows.map(row => ({ value: row.unitPriceInhouseMonthly[yearMonth] })), "value")?.toFixed(5)}</Td>)}
                <td>{/* レイアウト補正用 */}</td>
              </Tr>
              <Tr h={`${HEIGHT_ROW}px`}>
                <Td position="sticky" left={`${getFixedColumnLeft(9, true)}px`} zIndex="10" fontWeight="bold" fontSize={12}>{columnNames.materialCost.UnitPriceExternal}</Td>
                {yearMonthList.map((yearMonth) => <Td key={yearMonth} isTruncated={true} textAlign="right">{total(sheetData?.dataRows.map(row => ({ value: row.unitPriceExternalMonthly[yearMonth] })), "value")?.toFixed(5)}</Td>)}
                <td>{/* レイアウト補正用 */}</td>
              </Tr>
              <Tr h={`${HEIGHT_ROW}px`}>
                <Td position="sticky" left={`${getFixedColumnLeft(9, true)}px`} zIndex="10" fontWeight="bold" fontSize={12}>{columnNames.materialCost.InhouseRatio}</Td>
                {yearMonthList.map((yearMonth) => <Td key={yearMonth} isTruncated={true} textAlign="right">{displayUnit(multiply(sheetData?.dataRows.map(row => ({ value: row.inhouseRatioMonthly[yearMonth] })), "value")?.toString(), "%", 2)}</Td>)}
                <td>{/* レイアウト補正用 */}</td>
              </Tr>
              <Tr h={`${HEIGHT_ROW}px`}>
                <Td position="sticky" left={`${getFixedColumnLeft(9, true)}px`} zIndex="10" fontWeight="bold" fontSize={12}>{columnNames.materialCost.MaterialYield}</Td>
                {yearMonthList.map((yearMonth) => <Td key={yearMonth} isTruncated={true} textAlign="right">{displayUnit(multiply(sheetData?.dataRows.map(row => ({ value: row.materialYieldMonthly[yearMonth] })), "value")?.toString(), "%", 2)}</Td>)}
                <td>{/* レイアウト補正用 */}</td>
              </Tr>
              <Tr h={`${HEIGHT_ROW}px`}>
                <Td position="sticky" left={`${getFixedColumnLeft(9, true)}px`} zIndex="10" fontWeight="bold" fontSize={12}>{columnNames.materialCost.Amount}</Td>
                {yearMonthList.map((yearMonth) => <Td key={yearMonth} isTruncated={true} textAlign="right">{total(sheetData?.dataRows.map(row => ({ value: row.amountMonthly[yearMonth] })), "value")?.toFixed(5)}</Td>)}
                <td>{/* レイアウト補正用 */}</td>
              </Tr>
            </>
          ) : (
            <>
              {/* 折りたたみ時：1行のみ表示（金額） */}
              <Tr h={`${HEIGHT_ROW}px`}>
                <Td colSpan={getVisibleFixedColumnCount()} position="sticky" left={`${getFixedColumnLeft(1)}px`} zIndex="1" style={{ borderRightStyle: "none" }}></Td>
                <Td colSpan={2} position="sticky" left={`${getFixedColumnLeft(7, true)}px`} zIndex="1" fontWeight="bold" textAlign="right">
                  <HStack spacing={1} justifyContent="flex-end">
                    <Text>Total</Text>
                    <IconButton
                      aria-label={t('features.InvestmentScreening.BomInfoList.button.expand')}
                      icon={<ChevronUpIcon />}
                      size="lg"
                      maxH={25}
                      onClick={() => setIsMaterialCostTotalExpanded(true)}
                    />
                  </HStack>
                </Td>
                <Td position="sticky" left={`${getFixedColumnLeft(9, true)}px`} zIndex="10" fontWeight="bold" fontSize={12}>{columnNames.materialCost.Amount}</Td>
                {yearMonthList.map((yearMonth) => <Td key={yearMonth} isTruncated={true} textAlign="right">{total(sheetData?.dataRows.map(row => ({ value: row.amountMonthly[yearMonth] })), "value")?.toFixed(5)}</Td>)}
                <td>{/* レイアウト補正用 */}</td>
              </Tr>
            </>
          )}
        </Tfoot>
      </Table >
    );
  }

  /**
   * Cycletime テーブルコンポーネント
   * @param sheetData シートデータ
   * @returns 
   */
  const generateTableForCycletime = (sheetData: CycletimeSheetData | undefined) => {

    return (
      <Table variant="simple" size="sm" style={{ tableLayout: "fixed", borderCollapse: "separate" }} cellSpacing={0} className=" mm-ceas-table-extend">
        <colgroup>
          <col width={`${WIDTH_SEQ}px`} />
          {visibleColumns.level && <col width={`${WIDTH_LEVEL}px`} />}
          {visibleColumns.modelNo && <col width={`${WIDTH_MODEL_NO}px`} />}
          {visibleColumns.processCode && <col width={`${WIDTH_PROCESS_CODE}px`} />}
          {visibleColumns.itemNo && <col width={`${WIDTH_ITEM_NO}px`} />}
          {visibleColumns.itemName && <col width={`${WIDTH_ITEM_NAME}px`} />}
          {yearMonthList.map((yearMonth) => <col key={yearMonth} width={`${WIDTH_CYCLE_TIME}px`} />)}
          <col width="*" />
        </colgroup>
        <Thead position="sticky" top={0} bg="white" zIndex="10" >
          <Tr>
            <Th position="sticky" left={`${getFixedColumnLeft(1)}px`} zIndex="10" textTransform="none" rowSpan={2}>{columnNames.common.Seq}</Th>
            {visibleColumns.level && <Th position="sticky" left={`${getFixedColumnLeft(2)}px`} zIndex="10" textTransform="none" rowSpan={2}>{columnNames.common.Level}</Th>}
            {visibleColumns.modelNo && <Th position="sticky" left={`${getFixedColumnLeft(3)}px`} zIndex="10" textTransform="none" rowSpan={2}>{columnNames.common.ModelNo}</Th>}
            {visibleColumns.processCode && <Th position="sticky" left={`${getFixedColumnLeft(4)}px`} zIndex="10" textTransform="none" rowSpan={2}>{columnNames.common.ProcessCode}</Th>}
            {visibleColumns.itemNo && <Th position="sticky" left={`${getFixedColumnLeft(5)}px`} zIndex="10" textTransform="none" rowSpan={2}>{columnNames.common.ItemNo}</Th>}
            {visibleColumns.itemName && <Th position="sticky" left={`${getFixedColumnLeft(6)}px`} zIndex="10" textTransform="none" rowSpan={2} style={{ borderRight: "1px solid #ccc" }}>{columnNames.common.ItemName}</Th>}
            <Th position="sticky" left={`${getFixedColumnLeft(7)}px`} zIndex="10" textTransform="none" style={{ borderRightWidth: "0px" }}>{columnNames.cycletime.CycleTime}</Th>
            <Th colSpan={yearMonthList.length - 1} ></Th>
            <Th>{/* レイアウト補正用 */}</Th>
          </Tr>
          <Tr>
            {yearMonthList.map((yearMonth) => <Th key={yearMonth} textTransform="none">{yearMonth}</Th>)}
            <Th>{/* レイアウト補正用 */}</Th>
          </Tr>
        </Thead>
        <Tbody>
          {sheetData?.dataRows.map((row, idx) => {
            return (
              <Tr key={idx} h={`${HEIGHT_ROW}px`}>
                {renderErrorCell("cycletime", idx, "seq", row.seq, { position: "sticky", left: `${getFixedColumnLeft(1)}px`, zIndex: "1" })}
                {visibleColumns.level && renderErrorCell("cycletime", idx, "level", row.level, { position: "sticky", left: `${getFixedColumnLeft(2)}px`, zIndex: "1" })}
                {visibleColumns.modelNo && renderErrorCell("cycletime", idx, "modelNo", row.modelNo, { position: "sticky", left: `${getFixedColumnLeft(3)}px`, zIndex: "1" })}
                {visibleColumns.processCode && renderErrorCell("cycletime", idx, "processCode", row.processCode, { position: "sticky", left: `${getFixedColumnLeft(4)}px`, zIndex: "1" })}
                {visibleColumns.itemNo && renderErrorCell("cycletime", idx, "itemNo", row.itemNo, { position: "sticky", left: `${getFixedColumnLeft(5)}px`, zIndex: "1" })}
                {visibleColumns.itemName && renderErrorCell("cycletime", idx, "itemName", row.itemName, { position: "sticky", left: `${getFixedColumnLeft(6)}px`, zIndex: "1" })}
                {yearMonthList.map((yearMonth) => renderErrorCell("cycletime", idx, `monthlyData.${yearMonth}`, displayUnit(row.monthlyData[yearMonth], null, 5), { textAlign: "right", key: yearMonth }))}
                <td>{/* レイアウト補正用 */}</td>
              </Tr>
            )
          })}
        </Tbody>
        <Tfoot position="sticky" bottom={0} bg="white" zIndex="10">
          <Tr h={`${HEIGHT_ROW}px`}>
            {getVisibleFixedColumnCount() > 1 && (
              <Td position="sticky" left={`${getFixedColumnLeft(1)}px`} zIndex="1" colSpan={getVisibleFixedColumnCount() - 1}></Td>
            )}
            <Td position="sticky" left={`${getFixedTotalColumnLeft()}px`} zIndex="1" fontWeight="bold">Total</Td>
            {yearMonthList.map((yearMonth) => <Td key={yearMonth} isTruncated={true} textAlign="right">{displayUnit(total(sheetData?.dataRows.map(row => ({ value: row.monthlyData[yearMonth] })), "value")?.toString(), null, 5)}</Td>)}
            <td>{/* レイアウト補正用 */}</td>
          </Tr>
        </Tfoot>
      </Table>
    );
  }

  /**
   * Yield テーブルコンポーネント
   * @param sheetData シートデータ
   * @returns 
   */
  const generateTableForYield = (sheetData: YieldSheetData | undefined) => {

    return (
      <Table variant="simple" size="sm" style={{ tableLayout: "fixed", borderCollapse: "separate" }} cellSpacing={0} className=" mm-ceas-table-extend">
        <colgroup>
          <col width={`${WIDTH_SEQ}px`} />
          {visibleColumns.level && <col width={`${WIDTH_LEVEL}px`} />}
          {visibleColumns.modelNo && <col width={`${WIDTH_MODEL_NO}px`} />}
          {visibleColumns.processCode && <col width={`${WIDTH_PROCESS_CODE}px`} />}
          {visibleColumns.itemNo && <col width={`${WIDTH_ITEM_NO}px`} />}
          {visibleColumns.itemName && <col width={`${WIDTH_ITEM_NAME}px`} />}
          {yearMonthList.map((yearMonth) => <col key={yearMonth} width={`${WIDTH_YIELD}px`} />)}
          <col width="*" />
        </colgroup>
        <Thead position="sticky" top={0} bg="white" zIndex="10" >
          <Tr>
            <Th position="sticky" left={`${getFixedColumnLeft(1)}px`} zIndex="10" textTransform="none" rowSpan={2}>{columnNames.common.Seq}</Th>
            {visibleColumns.level && <Th position="sticky" left={`${getFixedColumnLeft(2)}px`} zIndex="10" textTransform="none" rowSpan={2}>{columnNames.common.Level}</Th>}
            {visibleColumns.modelNo && <Th position="sticky" left={`${getFixedColumnLeft(3)}px`} zIndex="10" textTransform="none" rowSpan={2}>{columnNames.common.ModelNo}</Th>}
            {visibleColumns.processCode && <Th position="sticky" left={`${getFixedColumnLeft(4)}px`} zIndex="10" textTransform="none" rowSpan={2}>{columnNames.common.ProcessCode}</Th>}
            {visibleColumns.itemNo && <Th position="sticky" left={`${getFixedColumnLeft(5)}px`} zIndex="10" textTransform="none" rowSpan={2}>{columnNames.common.ItemNo}</Th>}
            {visibleColumns.itemName && <Th position="sticky" left={`${getFixedColumnLeft(6)}px`} zIndex="10" textTransform="none" rowSpan={2}>{columnNames.common.ItemName}</Th>}
            <Th position="sticky" left={`${getFixedColumnLeft(7)}px`} zIndex="10" textTransform="none" style={{ borderRightWidth: "0px" }}>{columnNames.yield.Yield}</Th>
            <Th colSpan={yearMonthList.length - 1} ></Th>
            <Th>{/* レイアウト補正用 */}</Th>
          </Tr>
          {yearMonthList.map((yearMonth) => <Th key={yearMonth} textTransform="none">{yearMonth}</Th>)}
          <Th>{/* レイアウト補正用 */}</Th>
          <Tr>
          </Tr>
        </Thead>
        <Tbody>
          {sheetData?.dataRows.map((row, idx) => {
            return (
              <Tr key={idx} h={`${HEIGHT_ROW}px`}>
                {renderErrorCell("yield", idx, "seq", row.seq, { position: "sticky", left: `${getFixedColumnLeft(1)}px`, zIndex: "1" })}
                {visibleColumns.level && renderErrorCell("yield", idx, "level", row.level, { position: "sticky", left: `${getFixedColumnLeft(2)}px`, zIndex: "1" })}
                {visibleColumns.modelNo && renderErrorCell("yield", idx, "modelNo", row.modelNo, { position: "sticky", left: `${getFixedColumnLeft(3)}px`, zIndex: "1" })}
                {visibleColumns.processCode && renderErrorCell("yield", idx, "processCode", row.processCode, { position: "sticky", left: `${getFixedColumnLeft(4)}px`, zIndex: "1" })}
                {visibleColumns.itemNo && renderErrorCell("yield", idx, "itemNo", row.itemNo, { position: "sticky", left: `${getFixedColumnLeft(5)}px`, zIndex: "1" })}
                {visibleColumns.itemName && renderErrorCell("yield", idx, "itemName", row.itemName, { position: "sticky", left: `${getFixedColumnLeft(6)}px`, zIndex: "1" })}
                {yearMonthList.map((yearMonth) => renderErrorCell("yield", idx, `monthlyData.${yearMonth}`, row.monthlyData[yearMonth] ? displayUnit(row.monthlyData[yearMonth], "%", 2) : "", { textAlign: "right", key: yearMonth }))}
                <td>{/* レイアウト補正用 */}</td>
              </Tr>
            )
          })}
        </Tbody>
        <Tfoot position="sticky" bottom={0} bg="white" zIndex="10">
          <Tr h={`${HEIGHT_ROW}px`}>
            {getVisibleFixedColumnCount() > 1 && (
              <Td position="sticky" left={`${getFixedColumnLeft(1)}px`} zIndex="1" colSpan={getVisibleFixedColumnCount() - 1}></Td>
            )}
            <Td position="sticky" left={`${getFixedTotalColumnLeft()}px`} zIndex="1" fontWeight="bold">Total</Td>
            {yearMonthList.map((yearMonth) => <Td key={yearMonth} isTruncated={true} textAlign="right">{displayUnit(multiply(sheetData?.dataRows.map(row => ({ value: row.monthlyData[yearMonth] })), "value")?.toString(), "%", 2)}</Td>)}
            <td>{/* レイアウト補正用 */}</td>
          </Tr>
        </Tfoot>
      </Table>
    );
  }

  /**
   * レイアウト
   */
  return (
    <Modal
      isOpen={props.isOpen}
      onClose={props.onClose}
      blockScrollOnMount={true}
      preserveScrollBarGap={false}
      isCentered
      closeOnOverlayClick={false}
    >
      <ModalOverlay />
      <ModalContent maxW="1700px" maxH="93vh" overflow="auto">
        <ModalHeader display="flex" alignItems="center" gap={2}>
          {TITLE}
          <HelpIcon width="550px">
            {t(`${bl}.help.bomStructureHelp`)}
            {"\n\n"}
            {t(`${bl}.help.bomLevelNotationHelp`)}
            {"\n\n"}
            {t(`${bl}.help.bomNonSalesPartHelp`)}
            {"\n\n"}
            {t(`${bl}.help.bomMaterialColumnHelp`)}
          </HelpIcon>
        </ModalHeader>
        <ModalBody>
          {/* 上部ラベル表示 */}
          <Flex align="center" mb={4} gap={8}>
            <HStack spacing={2}>
              <Text fontWeight="bold">Part No:</Text>
              <Text>{resolveDisplayPartNo({ partNo: props.partNo || undefined, displayPartNo: props.displayPartNo || undefined }) || "-"}</Text>
            </HStack>
          </Flex>

          {/* タブ切り替えボタン */}
          <Flex align="center" gap={6} mb={4}>
            <ButtonGroup spacing={0} isAttached>
              {TABS.map((tab) => {
                // 当該タブに対応するシートにバリデーションエラーがあるか判定
                const tabHasError = Object.keys(validationErrors[tab.sheetKey] ?? {}).length > 0;
                return (
                  <Button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    variant={activeTab === tab.id ? "solid" : "outline"}
                    colorScheme={tabHasError ? "red" : (activeTab === tab.id ? "green" : "gray")}
                    borderRadius={0}
                    _first={{ borderLeftRadius: "md" }}
                    _last={{ borderRightRadius: "md" }}
                    leftIcon={tabHasError ? <FaExclamationCircle /> : undefined}
                    style={{ marginRight: tabHasError ? "0px" : "-1px" }}
                  >
                    {tab.label}
                  </Button>
                );
              })}
            </ButtonGroup>

            {/* 列選択ドロップダウン */}
            <Menu closeOnSelect={false}>
              <MenuButton as={Button} rightIcon={<ChevronDownIcon />} variant="outline" size="sm">
                {t(`${bl}.label.showColumns`)}
              </MenuButton>
              <MenuList minWidth="180px">
                {columnVisibilityOptions.map((col) => (
                  <MenuItem
                    key={col.key}
                    onClick={() => {
                      setVisibleColumns(prev => ({
                        ...prev,
                        [col.key]: !prev[col.key as keyof typeof prev]
                      }));
                    }}
                  >
                    <Checkbox
                      isChecked={visibleColumns[col.key as keyof typeof visibleColumns]}
                      pointerEvents="none"
                      mr={2}
                    />
                    {col.label}
                  </MenuItem>
                ))}
              </MenuList>
            </Menu>

            {/* サイクルタイム単位選択 */}
            <HStack spacing={2}>
              <Text fontWeight="bold" whiteSpace="nowrap">{t(`${bl}.label.cycleTimeUnit`)}:</Text>
              <RadioGroup
                value={cycleTimeUnit}
                isDisabled={!(props.pagePermissions?.isEditable || false)}
                onChange={(value: string) => setCycleTimeUnit(value as "minute" | "second" | "")}
              >
                <HStack spacing={4}>
                  <Radio value="minute">{t(`${bl}.label.minute`)}</Radio>
                  <Radio value="second">{t(`${bl}.label.second`)}</Radio>
                </HStack>
              </RadioGroup>
            </HStack>

            <Popover placement="left-start">
              <PopoverTrigger>
                {/* ？アイコン */}
                <button style={{ background: 'none', border: 'none', padding: 0 }}>
                  <FaRegQuestionCircle size="30px" cursor="pointer" />
                </button>
              </PopoverTrigger>
              <Portal>
                <PopoverContent width="500px" bgColor="#fffff0">
                  <PopoverArrow />
                  <PopoverHeader>
                    <Text fontWeight="bold">
                      {t(`${bl}.label.initRegistrationProcedure`)}
                    </Text>
                  </PopoverHeader>
                  <PopoverCloseButton />
                  <PopoverBody>
                    <OrderedList spacing={3}>
                      <ListItem><Button colorScheme="blue" size="xs">{t(`${bl}.button.processBomOnly`)}</Button>{t(`${bl}.procedure.pressToOpenSheet`)}</ListItem>
                      <ListItem>{t(`${bl}.procedure.spreadSheetOf`)}<Button color="blue" size="sm">ProcessBOM(initial)</Button>{t(`${bl}.procedure.enterSheetData`)}</ListItem>
                      <ListItem><Button colorScheme="blue" size="xs">{t(`${bl}.button.dataLink`)}</Button>{t(`${bl}.procedure.pressDataLink`)}<br />{t(`${bl}.procedure.selectCycleTimeUnitNote`)}</ListItem>
                      <ListItem><Button colorScheme="green" size="xs">ProcessBOM(initial)</Button>{t(`${bl}.procedure.selectMaterialRows`)}</ListItem>
                      <ListItem><Button colorScheme="blue" size="xs">{t(`${bl}.button.allSheets`)}</Button>{t(`${bl}.procedure.pressToOpenSheet`)}<br />{t(`${bl}.procedure.allSheetsNote`)}</ListItem>
                      <ListItem>{t(`${bl}.procedure.spreadSheetOf`)}<Button color="blue" size="sm">MaterialCost</Button>{t(`${bl}.procedure.enterRemainingSheets`)}</ListItem>
                      <ListItem><Button colorScheme="blue" size="xs">{t(`${bl}.button.dataLink`)}</Button>{t(`${bl}.procedure.pressDataLink`)}</ListItem>
                      <ListItem><Button colorScheme="blue" size="xs">{t('common.button.save')}</Button>{t(`${bl}.procedure.pressSave`)}</ListItem><br />
                    </OrderedList>
                  </PopoverBody>
                  <PopoverHeader>
                    <Text fontWeight="bold">
                      {t(`${bl}.label.changeCycleTimeUnit`)}
                    </Text>
                  </PopoverHeader>
                  <PopoverBody>
                    <OrderedList spacing={3}>
                      <ListItem><Button colorScheme="blue" size="xs">{t(`${bl}.button.allSheets`)}</Button>{t(`${bl}.procedure.pressToOpenSheet`)}</ListItem>
                      <ListItem><Button color="blue" size="sm">Cycletime</Button>{t(`${bl}.procedure.checkCycleTimeValue`)}</ListItem>
                      <ListItem>{t(`${bl}.procedure.selectCycleTimeUnit`)}</ListItem>
                      <ListItem><Button colorScheme="blue" size="xs">{t(`${bl}.button.dataLink`)}</Button>{t(`${bl}.procedure.pressDataLink`)}</ListItem>
                      <ListItem><Button colorScheme="blue" size="xs">{t('common.button.save')}</Button>{t(`${bl}.procedure.pressSave`)}</ListItem>
                      <Text>{t(`${bl}.procedure.cycleTimeUnitAutoConvertNotice`)}</Text>
                    </OrderedList>
                  </PopoverBody>
                </PopoverContent>
              </Portal>
            </Popover>
          </Flex>

          {/* データテーブル */}
          <TableContainer h="600px" overflowY="auto" overflowX="auto" border="1px solid #ccc">
            {activeTab === "ProcessBOM(initial)" && generateTableForProcessBOM(bomSheetsData?.processBom)}
            {activeTab === "MaterialCost" && generateTableForMaterialCost(bomSheetsData?.materialCost)}
            {activeTab === "Cycletime" && generateTableForCycletime(bomSheetsData?.cycletime)}
            {activeTab === "Yield" && generateTableForYield(bomSheetsData?.yield)}
          </TableContainer>
        </ModalBody>

        <ModalFooter>
          <HStack spacing={4}>
            {/* 読み取り専用でも値コピー用にGoogleSpreadは開けるようにする（データ連携・保存は不可） */}
            <Button colorScheme="blue" onClick={handleOpenProcessBomSheet} isLoading={isLoading}>
              {t(`${bl}.button.processBomOnly`)}
            </Button>
            <Tooltip
              label={t(`${bl}.tooltip.selectMaterialRow`)}
              isDisabled={isMaterialRowSelected}
              hasArrow
              placement="top"
              whiteSpace="pre-line"
            >
              <Button colorScheme="blue" onClick={handleOpenOtherSheets} isLoading={isLoading} isDisabled={!isMaterialRowSelected}>
                {t(`${bl}.button.allSheets`)}
              </Button>
            </Tooltip>
            <Tooltip
              label={
                !(props.pagePermissions?.isEditable || false)
                  ? t(`${bl}.tooltip.noEditPermission`)
                  : t(`${bl}.tooltip.openSpreadsheetFirst`)
              }
              isDisabled={(props.pagePermissions?.isEditable || false) && Boolean(spreadSheetWindows) && !isClosedSpreadSheetWindows}
              hasArrow
              placement="top"
              whiteSpace="pre-line"
            >
              <Button colorScheme="blue" onClick={handleDataLink} isLoading={isLoading} isDisabled={!(props.pagePermissions?.isEditable || false) || !Boolean(spreadSheetWindows) || isClosedSpreadSheetWindows}>
                {t(`${bl}.button.dataLink`)}
              </Button>
            </Tooltip>
            <Tooltip
              label={
                !(props.pagePermissions?.isEditable || false)
                  ? t(`${bl}.tooltip.noEditPermission`)
                  : (bomSheetsData?.processBom?.dataRows?.length || 0) === 0
                    ? t(`${bl}.tooltip.inputProcessBomData`)
                    : hasValidationErrors()
                      ? t(`${bl}.tooltip.resolveValidationErrors`)
                      : ""
              }
              isDisabled={
                (props.pagePermissions?.isEditable || false) &&
                (bomSheetsData?.processBom?.dataRows?.length || 0) > 0 &&
                !hasValidationErrors()
              }
              hasArrow
              placement="top"
              whiteSpace="pre-line"
            >
              <Button colorScheme="blue" onClick={handleSave} isLoading={isLoading} isDisabled={(bomSheetsData?.processBom?.dataRows?.length || 0) === 0 || hasValidationErrors() || !(props.pagePermissions?.isEditable || false)}>
                {t('common.button.save')}
              </Button>
            </Tooltip>
            <Button colorScheme="gray" onClick={handleClose} isLoading={isLoading}>
              {t('common.button.close')}
            </Button>
          </HStack>
        </ModalFooter >
      </ModalContent >

      {/* BOMシート作成ボタン押下時の確認ダイアログ（未連携変更ロスト防止） */}
      <AlertDialog
        isOpen={isConfirmDialogOpen}
        leastDestructiveRef={confirmDialogCancelRef}
        onClose={handleCancelOpenSpreadsheet}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              {t('common.button.confirm')}
            </AlertDialogHeader>
            <AlertDialogBody>
              {t(`${bl}.dialog.confirmOverwrite.body`)}
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button colorScheme="blue" onClick={handleConfirmOpenSpreadsheet}>
                {t(`${bl}.button.continueAnyway`)}
              </Button>
              <Button ref={confirmDialogCancelRef} onClick={handleCancelOpenSpreadsheet} ml={3}>
                {t('common.button.cancel')}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Modal >
  );
};

export default BomInfoList;
